import { useState, useEffect, useId, useRef, useMemo } from 'react'
import { createPortal } from 'react-dom'
import { deleteDataAPI, getDataAPI, getEffectiveUserId, postDataAPI, putDataAPI } from '../lib/api'
import { makeCacheKey } from '../lib/sessionCache'
import Button from '../components/ui/Button'
import Modal from '../components/ui/Modal'
import DatePicker from '../components/ui/DatePicker'
import { useLocation } from 'react-router-dom'
import { toLocalDateKey } from '../lib/dateUtils'
import { useUserPermissions } from '../hooks/useUserPermissions'
import * as XLSX from 'xlsx'
import * as pdfjsLib from 'pdfjs-dist'
import pdfjsWorkerUrl from 'pdfjs-dist/build/pdf.worker.min.mjs?url'

// Use the bundled worker so pdf.js can parse in a background thread.
pdfjsLib.GlobalWorkerOptions.workerSrc = pdfjsWorkerUrl

interface Show {
  id: string
  date?: string
  perf_date?: string
  title?: string
  event_title?: string
  venue?: string
  city?: string
  state?: string
  ticket_count?: number
  tix_sold?: number
  status?: string
  sellable_cap?: number
  tix_sold_percent?: number
  artist?: string
  source?: string
  guarantee?: string | number
  ticket_tracking?: boolean
  raw?: any
}

/** City → State/Province map for auto-fill (legacy behavior). */
const CITY_STATE_MAP: Record<string, string> = {
  'New York': 'NY',
  'Los Angeles': 'CA',
  Chicago: 'IL',
  Houston: 'TX',
  Phoenix: 'AZ',
  Philadelphia: 'PA',
  'San Antonio': 'TX',
  'San Diego': 'CA',
  Dallas: 'TX',
  'San Jose': 'CA',
  Austin: 'TX',
  Jacksonville: 'FL',
  'Fort Worth': 'TX',
  Columbus: 'OH',
  Charlotte: 'NC',
  'San Francisco': 'CA',
  Indianapolis: 'IN',
  Seattle: 'WA',
  Denver: 'CO',
  Washington: 'DC',
  Boston: 'MA',
  'El Paso': 'TX',
  Nashville: 'TN',
  Detroit: 'MI',
  'Oklahoma City': 'OK',
  Portland: 'OR',
  'Las Vegas': 'NV',
  Memphis: 'TN',
  Louisville: 'KY',
  Baltimore: 'MD',
  Milwaukee: 'WI',
  Albuquerque: 'NM',
  Tucson: 'AZ',
  Fresno: 'CA',
  Sacramento: 'CA',
  Mesa: 'AZ',
  'Kansas City': 'MO',
  Atlanta: 'GA',
  'Long Beach': 'CA',
  'Colorado Springs': 'CO',
  Raleigh: 'NC',
  Miami: 'FL',
  'Virginia Beach': 'VA',
  Omaha: 'NE',
  Oakland: 'CA',
  Minneapolis: 'MN',
  Tulsa: 'OK',
  Arlington: 'TX',
  Tampa: 'FL',
  'New Orleans': 'LA',
  Toronto: 'ON',
  Montreal: 'QC',
  Vancouver: 'BC',
  Calgary: 'AB',
  Edmonton: 'AB',
  Ottawa: 'ON',
  Winnipeg: 'MB',
  'Quebec City': 'QC',
  Hamilton: 'ON',
  Kitchener: 'ON',
  'London (ON)': 'ON',
  Victoria: 'BC',
  Halifax: 'NS',
  Oshawa: 'ON',
  Windsor: 'ON',
  London: 'England',
  Birmingham: 'England',
  Manchester: 'England',
  Glasgow: 'Scotland',
  Liverpool: 'England',
  Edinburgh: 'Scotland',
  Leeds: 'England',
  Sheffield: 'England',
  Bristol: 'England',
  Cardiff: 'Wales',
  Belfast: 'Northern Ireland',
  'Newcastle (UK)': 'England',
  Nottingham: 'England',
  Portsmouth: 'England',
  Plymouth: 'England',
  Sydney: 'NSW',
  Melbourne: 'VIC',
  Brisbane: 'QLD',
  Perth: 'WA',
  Adelaide: 'SA',
  'Gold Coast': 'QLD',
  'Newcastle (AU)': 'NSW',
  Canberra: 'ACT',
  'Sunshine Coast': 'QLD',
  Wollongong: 'NSW',
  Hobart: 'TAS',
  Geelong: 'VIC',
  Townsville: 'QLD',
  Berlin: 'Berlin',
  Hamburg: 'Hamburg',
  Munich: 'Bavaria',
  Cologne: 'North Rhine-Westphalia',
  Frankfurt: 'Hesse',
  Stuttgart: 'Baden-Württemberg',
  Düsseldorf: 'North Rhine-Westphalia',
  Dortmund: 'North Rhine-Westphalia',
  Essen: 'North Rhine-Westphalia',
  Leipzig: 'Saxony',
  Bremen: 'Bremen',
  Dresden: 'Saxony',
  Hanover: 'Lower Saxony',
  // France (legacy ManualAdd)
  Paris: 'Île-de-France',
  Marseille: 'Provence-Alpes-Côte d\'Azur',
  Lyon: 'Auvergne-Rhône-Alpes',
  Toulouse: 'Occitania',
  Nice: 'Provence-Alpes-Côte d\'Azur',
  Nantes: 'Pays de la Loire',
  Strasbourg: 'Grand Est',
  Montpellier: 'Occitania',
  Bordeaux: 'Nouvelle-Aquitaine',
  Lille: 'Hauts-de-France',
  Rennes: 'Brittany',
  Reims: 'Grand Est',
  // Italy
  Rome: 'Lazio',
  Milan: 'Lombardy',
  Naples: 'Campania',
  Turin: 'Piedmont',
  Palermo: 'Sicily',
  Genoa: 'Liguria',
  Bologna: 'Emilia-Romagna',
  Florence: 'Tuscany',
  Bari: 'Apulia',
  Catania: 'Sicily',
  Venice: 'Veneto',
  Verona: 'Veneto',
  // Spain
  Madrid: 'Community of Madrid',
  Barcelona: 'Catalonia',
  Valencia: 'Valencia',
  Seville: 'Andalusia',
  Zaragoza: 'Aragon',
  Málaga: 'Andalusia',
  Murcia: 'Murcia',
  Palma: 'Balearic Islands',
  'Las Palmas': 'Canary Islands',
  Bilbao: 'Basque Country',
  // Netherlands
  Amsterdam: 'North Holland',
  Rotterdam: 'South Holland',
  'The Hague': 'South Holland',
  Utrecht: 'Utrecht',
  Eindhoven: 'North Brabant',
  Tilburg: 'North Brabant',
  Groningen: 'Groningen',
  Almere: 'Flevoland',
  Breda: 'North Brabant',
  // Belgium
  Brussels: 'Brussels-Capital',
  Antwerp: 'Flanders',
  Ghent: 'East Flanders',
  Charleroi: 'Wallonia',
  Liège: 'Wallonia',
  Bruges: 'West Flanders',
  // Switzerland
  Zurich: 'Zurich',
  Geneva: 'Geneva',
  Basel: 'Basel-Stadt',
  Lausanne: 'Vaud',
  Bern: 'Bern',
  Winterthur: 'Zurich',
  Lucerne: 'Lucerne',
  // Austria
  Vienna: 'Vienna',
  Graz: 'Styria',
  Linz: 'Upper Austria',
  Salzburg: 'Salzburg',
  Innsbruck: 'Tyrol',
  // Sweden
  Stockholm: 'Stockholm',
  Gothenburg: 'Västra Götaland',
  Malmö: 'Skåne',
  Uppsala: 'Uppsala',
  Västerås: 'Västmanland',
  // Norway
  Oslo: 'Oslo',
  Bergen: 'Vestland',
  Stavanger: 'Rogaland',
  Trondheim: 'Trøndelag',
  // Denmark
  Copenhagen: 'Capital Region',
  Aarhus: 'Central Denmark',
  Odense: 'Southern Denmark',
  Aalborg: 'North Denmark',
  // Japan
  Tokyo: 'Tokyo',
  Osaka: 'Osaka',
  Yokohama: 'Kanagawa',
  Nagoya: 'Aichi',
  Sapporo: 'Hokkaido',
  Fukuoka: 'Fukuoka',
  Kobe: 'Hyogo',
  Kyoto: 'Kyoto',
  Kawasaki: 'Kanagawa',
  Saitama: 'Saitama',
  Hiroshima: 'Hiroshima',
  Sendai: 'Miyagi',
  // South Korea
  Seoul: 'Seoul',
  Busan: 'Busan',
  Incheon: 'Incheon',
  Daegu: 'Daegu',
  Daejeon: 'Daejeon',
  Gwangju: 'Gwangju',
  Ulsan: 'Ulsan',
  // Brazil
  'São Paulo': 'São Paulo',
  'Rio de Janeiro': 'Rio de Janeiro',
  Brasília: 'Federal District',
  Salvador: 'Bahia',
  Fortaleza: 'Ceará',
  'Belo Horizonte': 'Minas Gerais',
  Manaus: 'Amazonas',
  Curitiba: 'Paraná',
  Recife: 'Pernambuco',
  'Porto Alegre': 'Rio Grande do Sul',
  // Mexico
  'Mexico City': 'Mexico City',
  Guadalajara: 'Jalisco',
  Monterrey: 'Nuevo León',
  Puebla: 'Puebla',
  Tijuana: 'Baja California',
  León: 'Guanajuato',
  Juárez: 'Chihuahua',
  Zapopan: 'Jalisco',
  Mérida: 'Yucatán',
  Cancún: 'Quintana Roo',
  // Argentina
  'Buenos Aires': 'Buenos Aires',
  Córdoba: 'Córdoba',
  Rosario: 'Santa Fe',
  Mendoza: 'Mendoza',
  'La Plata': 'Buenos Aires',
  'Mar del Plata': 'Buenos Aires',
  // India
  Mumbai: 'Maharashtra',
  Delhi: 'Delhi',
  Bangalore: 'Karnataka',
  Hyderabad: 'Telangana',
  Chennai: 'Tamil Nadu',
  Kolkata: 'West Bengal',
  Pune: 'Maharashtra',
  Ahmedabad: 'Gujarat',
  Jaipur: 'Rajasthan',
  Surat: 'Gujarat',
  Lucknow: 'Uttar Pradesh',
  Kanpur: 'Uttar Pradesh',
  // China
  Shanghai: 'Shanghai',
  Beijing: 'Beijing',
  Shenzhen: 'Guangdong',
  Guangzhou: 'Guangdong',
  Chengdu: 'Sichuan',
  Tianjin: 'Tianjin',
  Wuhan: 'Hubei',
  Dongguan: 'Guangdong',
  Chongqing: 'Chongqing',
  Nanjing: 'Jiangsu',
  "Xi'an": 'Shaanxi',
  Hangzhou: 'Zhejiang',
  // Southeast Asia
  Bangkok: 'Thailand',
  'Chiang Mai': 'Thailand',
  Pattaya: 'Thailand',
  Phuket: 'Thailand',
  Singapore: 'Singapore',
  'Kuala Lumpur': 'Malaysia',
  Penang: 'Malaysia',
  Jakarta: 'Indonesia',
  Bali: 'Indonesia',
  Bandung: 'Indonesia',
  Surabaya: 'Indonesia',
  'Ho Chi Minh City': 'Vietnam',
  Hanoi: 'Vietnam',
  'Da Nang': 'Vietnam',
  Manila: 'Philippines',
  Cebu: 'Philippines',
  Yangon: 'Myanmar',
  'Phnom Penh': 'Cambodia',
  'Siem Reap': 'Cambodia',
  Taipei: 'Taiwan',
  'Hong Kong': 'Hong Kong',
  Macau: 'Macau',
  // Dominican Republic / Caribbean
  'Punta Cana': 'Dominican Republic',
  'Santo Domingo': 'Dominican Republic',
  Bimini: 'Bahamas',
  Nassau: 'Bahamas',
  Kingston: 'Jamaica',
  'San Juan': 'PR',
  // Middle East
  Dubai: 'UAE',
  'Abu Dhabi': 'UAE',
  Doha: 'Qatar',
  Riyadh: 'Saudi Arabia',
  Jeddah: 'Saudi Arabia',
  'Tel Aviv': 'Israel',
  Beirut: 'Lebanon',
  // Africa
  Lagos: 'Nigeria',
  Nairobi: 'Kenya',
  'Cape Town': 'Western Cape',
  Johannesburg: 'Gauteng',
  Marrakech: 'Morocco',
  Cairo: 'Egypt',
  Accra: 'Ghana',
  // New Zealand
  Auckland: 'Auckland',
  Wellington: 'Wellington',
  Christchurch: 'Canterbury',
  // Portugal
  Lisbon: 'Lisbon',
  Porto: 'Porto',
  // Poland
  Warsaw: 'Masovia',
  Krakow: 'Lesser Poland',
  // Czech Republic
  Prague: 'Prague',
  // Hungary
  Budapest: 'Budapest',
  // Romania
  Bucharest: 'Bucharest',
  // Greece
  Athens: 'Attica',
  Thessaloniki: 'Central Macedonia',
  Mykonos: 'South Aegean',
  // Croatia
  Zagreb: 'Zagreb',
  Split: 'Split-Dalmatia',
  Dubrovnik: 'Dubrovnik-Neretva',
  // Alternate spellings / no-accent versions for import matching
  'Sao Paulo': 'São Paulo',
  'Cancun': 'Quintana Roo',
  'Merida': 'Yucatán',
  'Bogota': 'Colombia',
  'Medellin': 'Colombia',
  'Dusseldorf': 'North Rhine-Westphalia',
  'Malaga': 'Andalusia',
  'Park City': 'UT',
  'Pomona': 'CA',
  'Weston': 'MA',
}

const SHOW_AUTOFILL_STORAGE_KEY = 'dizee_shows_field_suggestions'
const MAX_SUGGESTIONS = 50

const BANDSINTOWN_CONNECTION_KEY = 'dizee_bandsintown_connection'

function getBandsintownConnection(): { appId: string; artist: string } | null {
  try {
    const raw = localStorage.getItem(BANDSINTOWN_CONNECTION_KEY)
    if (!raw) return null
    const obj = JSON.parse(raw)
    if (obj && typeof obj.appId === 'string' && obj.appId.trim()) {
      return { appId: obj.appId.trim(), artist: typeof obj.artist === 'string' ? obj.artist.trim() : '' }
    }
    return null
  } catch {
    return null
  }
}

function setBandsintownConnection(appId: string, artist: string) {
  try {
    localStorage.setItem(
      BANDSINTOWN_CONNECTION_KEY,
      JSON.stringify({ appId: appId.trim(), artist: artist.trim() })
    )
  } catch {
    // ignore
  }
}

function clearBandsintownConnection() {
  try {
    localStorage.removeItem(BANDSINTOWN_CONNECTION_KEY)
  } catch {
    // ignore
  }
}

function getShowFieldSuggestions(fieldKey: string): string[] {
  try {
    const raw = localStorage.getItem(SHOW_AUTOFILL_STORAGE_KEY)
    if (!raw) return []
    const obj = JSON.parse(raw) as Record<string, string[]>
    const list = obj[fieldKey]
    return Array.isArray(list) ? list.slice(0, MAX_SUGGESTIONS) : []
  } catch {
    return []
  }
}

function addShowFieldSuggestion(fieldKey: string, value: string) {
  const v = String(value).trim()
  if (!v) return
  try {
    const raw = localStorage.getItem(SHOW_AUTOFILL_STORAGE_KEY)
    const obj: Record<string, string[]> = raw ? JSON.parse(raw) : {}
    const list = obj[fieldKey] ?? []
    const next = [v, ...list.filter((x) => x !== v)].slice(0, MAX_SUGGESTIONS)
    obj[fieldKey] = next
    localStorage.setItem(SHOW_AUTOFILL_STORAGE_KEY, JSON.stringify(obj))
  } catch {
    // ignore
  }
}

/** Bandsintown icon from legacy src/svg/EventPlatform/Bandsintown.jsx */
function BandsintownBadge() {
  const clipId = useId()
  return (
    <span className="inline-flex flex-shrink-0 self-center" title="From Bandsintown">
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width={14}
        height={14}
        viewBox="0 0 15 15"
        fill="none"
        aria-hidden
      >
        <g clipPath={`url(#${clipId})`}>
          <path
            fill="#00CEC8"
            d="M11.74 0H15v7.5h-3.26V0ZM7.825 3.125h3.26V7.5h-3.26V3.125Zm-3.913 0h3.26V7.5h-3.26V3.125ZM15 15H0V0h3.26v11.875h8.48v-.625H3.911V8.125H15V15Z"
          />
        </g>
        <defs>
          <clipPath id={clipId}>
            <path fill="#fff" d="M0 0h15v15H0z" />
          </clipPath>
        </defs>
      </svg>
    </span>
  )
}

type FieldType = 'textarea' | 'checkbox' | 'select' | 'date' | 'number'

type Field = {
  key: string
  label: string
  type?: FieldType
  required?: boolean
  options?: string[]
  placeholder?: string
  fullWidth?: boolean
  /** Group key — fields sharing the same group render together in one full-width row */
  group?: string
}

export default function ShowsPage() {
  // Navigate not used yet - kept for future use
  // const _navigate = useNavigate()
  const location = useLocation()
  // Scope cache keys by the effective user so team members don't see stale data
  // from a previously-viewed artist account.
  const _effectiveId = getEffectiveUserId() || 'self'
  const showsCacheKey = `showsPageCache:${_effectiveId}`
  const trackingOverridesKey = `showsTicketTrackingOverrides:${_effectiveId}`

  /** Read ticket-tracking overrides the user has explicitly set (keyed by show ID). */
  const readTrackingOverrides = (): Record<string, boolean> => {
    try {
      const raw = localStorage.getItem(trackingOverridesKey)
      return raw ? JSON.parse(raw) : {}
    } catch { return {} }
  }

  /** Persist a ticket-tracking override for a specific show. */
  const writeTrackingOverride = (showId: string, value: boolean) => {
    const map = readTrackingOverrides()
    map[showId] = value
    try { localStorage.setItem(trackingOverridesKey, JSON.stringify(map)) } catch { /* noop */ }
  }
  // ── Granular permissions for team members ──
  const { isViewOnly, getShowsConfig } = useUserPermissions()
  const showsConfig = getShowsConfig()
  const isShowsViewOnly = isViewOnly('shows')
  const hideTickets = showsConfig?.ticketAccess === false

  const [shows, setShows] = useState<Show[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [isAddOpen, setIsAddOpen] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [showAdvanced, setShowAdvanced] = useState(false)
  const [addView, setAddView] = useState<'options' | 'manual' | 'csv' | 'bandsintown' | 'ticket-counter'>(
    'options'
  )

  // ── Dizee-sourced artist name (from Spotify onboarding). This is the source of truth. ──
  const [dizeeArtistName, setDizeeArtistName] = useState<string>('')

  // Mirrors legacy Bookings/ManualAdd.jsx fields
  const [editData, setEditData] = useState<Record<string, any>>({
    artist: '',
    perf_date: toLocalDateKey(new Date()),
    city: '',
    state: '',
    venue: '',
    event_title: '',
    status: 'pending',
    show_time: '',
    announce_date: '',
    on_sale_date: '',
    guarantee: '',
    walkout_potential: '',
    sellable_cap: '',
    promoter: '',
    tix_sold_percent: '',
    tix_sold: '',
    // advanced
    appearing_with: '',
    red_flag: false,
    red_flag_notes: '',
    promoter_company: '',
    promoter_contact_names: '',
    promoter_contact_emails: '',
    venue_category: '',
    billing_type: '',
    territory: '',
    advanced_info: '',
    deal_type: '',
    deal_info: '',
    gross_potential: '',
    net_potential: '',
    artist_net: '',
    artist_profit: '',
    split_point: '',
    amount_to_split: '',
    ticket_notes: '',
    ticket_prices_tiers: '',
    ticket_tracking: true,
    ticket_taxes_fees: '',
    total_fees: '',
    deposit_due: '',
    deposit_received: '',
    contract_status: '',
    currency_conversion: '',
    additional_clauses: '',
    schedule: '',
    notes: '',
  })
  const [csvFileName, setCsvFileName] = useState<string | null>(null)
  const [csvRows, setCsvRows] = useState<Record<string, any>[]>([])
  const [csvLoading, setCsvLoading] = useState(false)
  const [csvImporting, setCsvImporting] = useState(false)
  const [csvError, setCsvError] = useState<string | null>(null)
  const [bandsintownArtist, setBandsintownArtist] = useState('')
  const [bandsintownAppId, setBandsintownAppId] = useState('')
  const [bandsintownEvents, setBandsintownEvents] = useState<Record<string, any>[]>([])
  const [bandsintownLoading, setBandsintownLoading] = useState(false)
  const [bandsintownImporting, setBandsintownImporting] = useState(false)
  const [bandsintownError, setBandsintownError] = useState<string | null>(null)
  const [ticketCounts, setTicketCounts] = useState<Record<string, string>>({})
  const [ticketPercentages, setTicketPercentages] = useState<Record<string, string>>({})
  const [sellableCaps, setSellableCaps] = useState<Record<string, string>>({})
  const [ticketSaving, setTicketSaving] = useState<Record<string, boolean>>({})
  const [ticketDirty, setTicketDirty] = useState<Set<string>>(new Set())
  // ── Ticket Import state ──
  const [ticketImportMode, setTicketImportMode] = useState(false)
  const [ticketImportText, setTicketImportText] = useState('')
  const [ticketImportParsing, setTicketImportParsing] = useState(false)
  const [ticketImportError, setTicketImportError] = useState<string | null>(null)
  const [ticketImportFileName, setTicketImportFileName] = useState<string | null>(null)
  const [ticketImportMatches, setTicketImportMatches] = useState<
    Array<{ showId: string; showLabel: string; count: number; percent?: number }>
  >([])
  const ticketImportFileRef = useRef<HTMLInputElement>(null)
  const [statusSaving, setStatusSaving] = useState<Record<string, boolean>>({})
  const [openDropdown, setOpenDropdown] = useState<string | null>(null)
  const [dropdownPosition, setDropdownPosition] = useState<{ top: number; right: number } | null>(null)
  const [isEditing, setIsEditing] = useState(false)
  const [editShowId, setEditShowId] = useState<string | null>(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [statusFilter, setStatusFilter] = useState<string>('')
  const [selectedShowIds, setSelectedShowIds] = useState<Set<string>>(new Set())
  const [isBulkDeleting, setIsBulkDeleting] = useState(false)
  const [cityOptions, setCityOptions] = useState<string[]>([])
  const [showCityDropdown, setShowCityDropdown] = useState(false)
  const [artistFieldUnlocked, setArtistFieldUnlocked] = useState(false)

  // ── Duplicate Detection Modal state ──
  const [showDuplicateModal, setShowDuplicateModal] = useState(false)
  const [duplicateRows, setDuplicateRows] = useState<Array<{ incoming: Record<string, any>; existing: Show }>>([])
  const [nonDuplicateRows, setNonDuplicateRows] = useState<Record<string, any>[]>([])
  const [duplicateMergeSet, setDuplicateMergeSet] = useState<Set<number>>(new Set())
  const [duplicateImporting, setDuplicateImporting] = useState(false)

  // ── CSV artist name override (editable by user, defaults to dizeeArtistName) ──
  const [csvArtistOverride, setCsvArtistOverride] = useState<string>('')

  // ── Bandsintown Approval Modal state ──
  const [pendingBITShows, setPendingBITShows] = useState<Record<string, any>[]>([])
  const [pendingBITApproved, setPendingBITApproved] = useState<Set<number>>(new Set())
  const [showBITApprovalModal, setShowBITApprovalModal] = useState(false)
  const [bitApprovalSaving, setBitApprovalSaving] = useState(false)

  // Accepts common CSV/Excel-ish date strings and returns canonical YYYY-MM-DD.
  // Handles suffixes/prefixes like "09.13.2025 Sat (DNA)" so we don't show "Invalid Date".
  const normalizeLooseDate = (raw: string): string | undefined => {
    const value = raw.trim()
    if (!value) return undefined

    const token =
      value.match(/^(\d{1,4}[./-]\d{1,2}[./-]\d{1,4})/)?.[1] ??
      value.match(/(\d{1,4}[./-]\d{1,2}[./-]\d{1,4})/)?.[1] ??
      value

    const patterns: Array<{ re: RegExp; kind: 'mdy' | 'dmy' | 'ymd' }> = [
      { re: /^(\d{1,2})\.(\d{1,2})\.(\d{2,4})$/, kind: 'mdy' }, // MM.DD.YYYY or MM.DD.YY
      { re: /^(\d{1,2})\/(\d{1,2})\/(\d{2,4})$/, kind: 'mdy' }, // MM/DD/YYYY or MM/DD/YY
      { re: /^(\d{1,2})-(\d{1,2})-(\d{2,4})$/, kind: 'dmy' }, // DD-MM-YYYY or DD-MM-YY
      { re: /^(\d{4})-(\d{1,2})-(\d{1,2})$/, kind: 'ymd' }, // YYYY-MM-DD
      { re: /^(\d{4})\/(\d{1,2})\/(\d{1,2})$/, kind: 'ymd' }, // YYYY/MM/DD
      { re: /^(\d{4})\.(\d{1,2})\.(\d{1,2})$/, kind: 'ymd' }, // YYYY.MM.DD
    ]

    for (const p of patterns) {
      const m = token.match(p.re)
      if (!m) continue

      let year = ''
      let month = ''
      let day = ''

      if (p.kind === 'ymd') {
        year = m[1]
        month = m[2]
        day = m[3]
      } else if (p.kind === 'mdy') {
        month = m[1]
        day = m[2]
        year = m[3]
      } else {
        day = m[1]
        month = m[2]
        year = m[3]
      }

      const yearNumRaw = Number(year)
      const yearNum = year.length === 2 ? 2000 + yearNumRaw : yearNumRaw
      const mmNum = Number(month)
      const ddNum = Number(day)
      const yy = String(yearNum)
      const mm = String(mmNum).padStart(2, '0')
      const dd = String(ddNum).padStart(2, '0')

      // Validate as a real calendar date.
      const d = new Date(yearNum, mmNum - 1, ddNum, 12, 0, 0, 0)
      if (d.getFullYear() === yearNum && d.getMonth() === mmNum - 1 && d.getDate() === ddNum) {
        return `${yy}-${mm}-${dd}`
      }
    }

    const cleaned = token.replace(/^(mon|tue|tues|wed|thu|thur|thurs|fri|sat|sun)\s*,?\s*/i, '')
    const parsed = new Date(cleaned)
    if (!Number.isNaN(parsed.getTime())) {
      const yy = parsed.getFullYear()
      const mm = String(parsed.getMonth() + 1).padStart(2, '0')
      const dd = String(parsed.getDate()).padStart(2, '0')
      return `${yy}-${mm}-${dd}`
    }

    return undefined
  }

  const toLocalDate = (raw?: unknown): Date | null => {
    if (raw === undefined || raw === null) return null
    const str = String(raw).trim()
    if (!str) return null

    const normalized = normalizeLooseDate(str)
    if (normalized) {
      const m = normalized.match(/^(\d{4})-(\d{2})-(\d{2})$/)
      if (m) {
        const y = Number(m[1])
        const mo = Number(m[2])
        const da = Number(m[3])
        const d = new Date(y, mo - 1, da, 12, 0, 0, 0)
        if (!Number.isNaN(d.getTime())) return d
      }
    }

    const parsed = new Date(str)
    if (!Number.isNaN(parsed.getTime())) return parsed
    return null
  }

  const getDateTime = (raw?: unknown): number => {
    const d = toLocalDate(raw)
    return d ? d.getTime() : Number.MAX_SAFE_INTEGER
  }

  const formatDateForUI = (raw?: unknown, fallback: string = '—') => {
    const d = toLocalDate(raw)
    return d ? d.toLocaleDateString() : fallback
  }

  const basicFields: Field[] = [
    { key: 'artist', label: 'Artist', required: true, fullWidth: true },
    // Row: Performance Date | Status
    { key: 'perf_date', label: 'Performance Date', type: 'date', required: true },
    { key: 'status', label: 'Status', type: 'select', options: ['pending', 'confirmed', 'announced', 'private', 'canceled'] },
    // Row: City | State/Province
    { key: 'city', label: 'City', required: true },
    { key: 'state', label: 'State/Province', required: true },
    // Row: Venue | Event Title
    { key: 'venue', label: 'Venue', required: true },
    { key: 'event_title', label: 'Event Title' },
    // Row: Announce Date | On Sale Date
    { key: 'announce_date', label: 'Announce Date', type: 'date' },
    { key: 'on_sale_date', label: 'On Sale Date', type: 'date' },
    // Row: Guarantee | Walkout
    { key: 'guarantee', label: 'Guarantee', placeholder: 'Enter amount' },
    { key: 'walkout_potential', label: 'Walkout', placeholder: 'Enter amount' },
    // Row: Sellable Cap | Tix Sold | % Tix Sold (3 across)
    { key: 'sellable_cap', label: 'Sellable Cap', type: 'number', placeholder: 'Enter capacity', group: 'tickets' },
    { key: 'tix_sold', label: 'Tix Sold', type: 'number', group: 'tickets' },
    { key: 'tix_sold_percent', label: '% Tix Sold', type: 'number', group: 'tickets' },
    // Row: Promoter | Set Time
    { key: 'promoter', label: 'Promoter' },
    { key: 'show_time', label: 'Set Time', placeholder: 'e.g., 9:00 PM' },
    // Appearing With (full width)
    { key: 'appearing_with', label: 'Appearing With', fullWidth: true },
  ] as const

  const advancedFields: Field[] = [
    { key: 'red_flag', label: 'Red Flag', type: 'checkbox' },
    { key: 'red_flag_notes', label: 'Red Flag Notes', type: 'textarea' },
    { key: 'promoter_company', label: 'Promoter Company' },
    { key: 'promoter_contact_names', label: 'Promoter Contact Name(s)' },
    { key: 'promoter_contact_emails', label: 'Promoter Contact Email(s)' },
    { key: 'venue_category', label: 'Venue Category' },
    { key: 'billing_type', label: 'Billing Type' },
    { key: 'territory', label: 'Territory' },
    { key: 'advanced_info', label: 'Advanced Info', type: 'textarea' },
    { key: 'deal_type', label: 'Deal Type' },
    { key: 'deal_info', label: 'Deal Info' },
    { key: 'gross_potential', label: 'Gross Potential' },
    { key: 'net_potential', label: 'Net Potential' },
    { key: 'artist_net', label: 'Artist Net' },
    { key: 'artist_profit', label: 'Artist Profit' },
    { key: 'split_point', label: 'Split Point' },
    { key: 'amount_to_split', label: 'Amount to Split' },
    { key: 'ticket_notes', label: 'Ticket Notes', type: 'textarea' },
    { key: 'ticket_prices_tiers', label: 'Ticket Prices (tiers)' },
    { key: 'ticket_taxes_fees', label: 'Ticket Taxes / Fees' },
    { key: 'total_fees', label: 'Total Fees' },
    { key: 'deposit_due', label: 'Deposit Due' },
    { key: 'deposit_received', label: 'Deposit Received' },
    { key: 'contract_status', label: 'Contract Status' },
    { key: 'currency_conversion', label: 'Currency / Conversion' },
    { key: 'additional_clauses', label: 'Additional Clauses', type: 'textarea' },
    { key: 'schedule', label: 'Schedule' },
    { key: 'notes', label: 'Notes', type: 'textarea' },
  ] as const

  // Legacy-style city field: show dropdown of matching cities, select one to set city + state
  const handleCityChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setEditData((prev) => ({ ...prev, city: value }))
    if (value.length >= 2) {
      const matches = Object.keys(CITY_STATE_MAP).filter((city) =>
        city.toLowerCase().includes(value.toLowerCase())
      )
      setCityOptions(matches)
      setShowCityDropdown(matches.length > 0)
    } else {
      setCityOptions([])
      setShowCityDropdown(false)
    }
  }

  const selectCity = (city: string) => {
    const state = CITY_STATE_MAP[city]
    setEditData((prev) => ({ ...prev, city, state: state ?? '' }))
    setShowCityDropdown(false)
    setCityOptions([])
  }

  const renderField = (field: Field) => {
    const value = editData[field.key] ?? ''
    const common = 'ds-input'

    if (field.type === 'textarea') {
      return (
        <textarea
          value={value}
          onChange={(e) => setEditData((prev) => ({ ...prev, [field.key]: e.target.value }))}
          placeholder={field.placeholder}
          className={`${common} min-h-[90px]`}
        />
      )
    }

    if (field.type === 'checkbox') {
      return (
        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={!!value}
            onChange={(e) => setEditData((prev) => ({ ...prev, [field.key]: e.target.checked }))}
            className="h-4 w-4"
          />
          <span className="text-sm text-gray-600">Yes</span>
        </div>
      )
    }

    if (field.type === 'select') {
      return (
        <select
          value={value}
          onChange={(e) => setEditData((prev) => ({ ...prev, [field.key]: e.target.value }))}
          className={common}
        >
          {(field.options || []).map((opt: string) => (
            <option key={opt} value={opt}>
              {opt}
            </option>
          ))}
        </select>
      )
    }

    if (field.type === 'date') {
      return (
        <DatePicker
          value={value}
          onChange={(newValue) => setEditData((prev) => ({ ...prev, [field.key]: newValue }))}
          placeholder="yyyy-mm-dd"
          required={!!field.required}
        />
      )
    }

    // Legacy-style city field: dropdown of matching cities; selecting one sets city + state
    if (field.key === 'city') {
      return (
        <div className="relative">
          <input
            type="text"
            value={value}
            required={!!field.required}
            onChange={handleCityChange}
            onBlur={() => setTimeout(() => setShowCityDropdown(false), 150)}
            placeholder={field.placeholder}
            className={common}
            list={`shows-dl-${field.key}`}
            autoComplete="off"
          />
          <datalist id={`shows-dl-${field.key}`}>
            {getShowFieldSuggestions(field.key).map((opt) => (
              <option key={opt} value={opt} />
            ))}
          </datalist>
          {showCityDropdown && cityOptions.length > 0 && (
            <div className="absolute z-20 left-0 right-0 mt-1 max-h-[180px] overflow-y-auto rounded-lg border border-gray-200 bg-white shadow-lg dark:border-gray-600 dark:bg-gray-800">
              {cityOptions.map((city) => (
                <button
                  key={city}
                  type="button"
                  className="flex w-full items-center justify-between px-3 py-2 text-left text-sm hover:bg-gray-100 dark:hover:bg-gray-700 border-b border-gray-100 dark:border-gray-700 last:border-b-0"
                  onClick={() => selectCity(city)}
                >
                  <span className="text-gray-900 dark:text-gray-100">{city}</span>
                  <span className="text-gray-500 dark:text-gray-400 text-xs">
                    {CITY_STATE_MAP[city]}
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>
      )
    }

    // ── Artist field: pre-filled from Dizee with option to override ──
    if (field.key === 'artist' && dizeeArtistName && !isEditing) {
      const isLocked = !artistFieldUnlocked
      if (isLocked) {
        return (
          <div className="flex items-center gap-2">
            <input
              type="text"
              value={dizeeArtistName}
              readOnly
              className={`${common} flex-1 bg-gray-50 text-gray-600 cursor-default`}
            />
            <button
              type="button"
              onClick={() => {
                setArtistFieldUnlocked(true)
                setEditData((prev) => ({ ...prev, artist: dizeeArtistName }))
              }}
              className="flex-shrink-0 p-2 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
              title="Edit artist name"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L6.832 19.82a4.5 4.5 0 01-1.897 1.13l-2.685.8.8-2.685a4.5 4.5 0 011.13-1.897L16.863 4.487z" />
              </svg>
            </button>
          </div>
        )
      }
      // Unlocked: editable input with a reset button to go back to the Dizee value
      return (
        <div className="flex items-center gap-2">
          <input
            type="text"
            value={editData.artist ?? ''}
            required
            onChange={(e) => setEditData((prev) => ({ ...prev, artist: e.target.value }))}
            placeholder={dizeeArtistName}
            className={`${common} flex-1`}
            autoComplete="off"
          />
          <button
            type="button"
            onClick={() => {
              setArtistFieldUnlocked(false)
              setEditData((prev) => ({ ...prev, artist: dizeeArtistName }))
            }}
            className="flex-shrink-0 p-2 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
            title="Reset to Dizee artist name"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 15L3 9m0 0l6-6M3 9h12a6 6 0 010 12h-3" />
            </svg>
          </button>
        </div>
      )
    }

    const inputType = field.type === 'number' ? 'number' : 'text'
    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      setEditData((prev) => ({ ...prev, [field.key]: e.target.value }))
    }
    const datalistId = `shows-dl-${field.key}`
    const suggestions = getShowFieldSuggestions(field.key)
    return (
      <>
        <input
          type={inputType}
          value={value}
          required={!!field.required}
          onChange={handleInputChange}
          placeholder={field.placeholder}
          className={common}
          list={datalistId}
          autoComplete="off"
        />
        <datalist id={datalistId}>
          {suggestions.map((opt) => (
            <option key={opt} value={opt} />
          ))}
        </datalist>
      </>
    )
  }

  const normalizeBookings = (payload: any) => {
    const list = payload?.data?.data || payload?.data || payload || []
    const array = Array.isArray(list) ? list : Array.isArray(list?.data) ? list.data : []
    const trackingOverrides = readTrackingOverrides()
    return array.map((item: any) => {
      let city = item.city
      let state = item.state ?? item.state_province
      if ((!city || !state) && item.city_state) {
        const parts = String(item.city_state).split(',').map((s: string) => s.trim())
        if (!city && parts[0]) city = parts[0]
        if (!state && parts[1]) state = parts[1]
      }
      const rawPerf = (item.perf_date ?? item.date ?? '').toString?.() ?? String(item.perf_date ?? item.date ?? '')
      const normalizedPerf = rawPerf ? normalizeLooseDate(rawPerf) : undefined
      const eventTitle = item.event_title || item.title || ''
      const venueVal = item.venue || ''
      const showId = item._id || item.id

      // Resolve ticket_tracking:
      // 1. Check localStorage overrides first (user explicitly toggled tracking on/off)
      // 2. Then honour explicit values from the backend
      // 3. Fall back to auto-detect for festivals
      let tracking: boolean
      if (showId && trackingOverrides[showId] !== undefined) {
        tracking = trackingOverrides[showId]
      } else if (item.ticket_tracking === false || item.ticket_tracking === 'false' || item.ticket_tracking === 0) {
        tracking = false
      } else if (item.ticket_tracking === true || item.ticket_tracking === 'true' || item.ticket_tracking === 1) {
        tracking = true
      } else {
        // Not explicitly set — auto-detect festivals and default them to not-tracked
        const lowerTitle = String(eventTitle).toLowerCase()
        const lowerVenue = String(venueVal).toLowerCase()
        tracking = !(lowerTitle.includes('festival') || lowerVenue.includes('festival'))
      }

      return {
      id: item._id || item.id,
      date: normalizedPerf || item.date || item.perf_date,
      perf_date: normalizedPerf || item.perf_date || item.date,
      title: eventTitle || venueVal,
      event_title: item.event_title || item.title,
      venue: venueVal,
      city,
      state,
      artist: item.artist || item.artist_name,
      tix_sold: item.tix_sold ?? item.current_ticket_count ?? item.tickets_sold ?? item.ticket_count ?? 0,
      // Keep legacy field for read-compat and any downstream UI still expecting it.
      ticket_count:
        item.ticket_count ??
        item.tickets_sold ??
        item.tix_sold ??
        item.current_ticket_count ??
        0,
      status: item.status,
      sellable_cap: item.sellable_cap ?? item.capacity ?? item.cap ?? item.venue_capacity,
      tix_sold_percent: item.tix_sold_percent ?? item.percentage_sold,
      guarantee: item.guarantee,
      source: item.source,
      ticket_tracking: tracking,
      raw: item,
    }
    })
  }

  const resetAddModal = () => {
    setAddView('options')
    setShowAdvanced(false)
    setCsvFileName(null)
    setCsvRows([])
    setCsvLoading(false)
    setCsvImporting(false)
    setCsvError(null)
    setBandsintownArtist('')
    setBandsintownAppId('')
    setBandsintownEvents([])
    setIsEditing(false)
    setEditShowId(null)
    setOpenDropdown(null)
    setDropdownPosition(null)
    setBandsintownLoading(false)
    setBandsintownImporting(false)
    setBandsintownError(null)
    setCityOptions([])
    setShowCityDropdown(false)
    setArtistFieldUnlocked(false)
    // Reset CSV artist override back to the Dizee artist name
    setCsvArtistOverride(dizeeArtistName)
    // Reset ticket-import state
    setTicketImportMode(false)
    setTicketImportText('')
    setTicketImportParsing(false)
    setTicketImportError(null)
    setTicketImportFileName(null)
    setTicketImportMatches([])
    // Reset manual-add form fields, preserving the Dizee artist name
    setEditData((prev) => ({
      ...Object.fromEntries(Object.keys(prev).map((k) => [k, k === 'perf_date' ? toLocalDateKey(new Date()) : k === 'status' ? 'pending' : k === 'red_flag' ? false : k === 'ticket_tracking' ? true : ''])),
      artist: dizeeArtistName,
    }))
  }

  /** True when the show is actively tracking ticket sales. */
  const isTrackingTickets = (show: Show): boolean =>
    show.ticket_tracking !== false && (show.ticket_tracking as any) !== 'false'

  const getTicketCount = (show: Show) => {
    const raw = show.raw ?? {}
    const val =
      show.tix_sold ??
      show.ticket_count ??
      raw.ticket_count ??
      raw.tickets_sold ??
      raw.tix_sold ??
      0
    const n = typeof val === 'number' ? val : Number(val)
    return Number.isNaN(n) ? 0 : Math.max(0, n)
  }

  const readCachedShows = () => {
    if (typeof window === 'undefined') return null
    try {
      const raw = window.localStorage.getItem(showsCacheKey)
      if (!raw) return null
      const parsed = JSON.parse(raw)
      if (!parsed?.shows || !Array.isArray(parsed.shows)) return null
      return parsed.shows as Show[]
    } catch (error) {
      console.warn('Failed to read shows cache:', error)
      return null
    }
  }

  const writeCachedShows = (cachedShows: Show[]) => {
    if (typeof window === 'undefined') return
    try {
      window.localStorage.setItem(
        showsCacheKey,
        JSON.stringify({ timestamp: Date.now(), shows: cachedShows })
      )
    } catch (error) {
      console.warn('Failed to write shows cache:', error)
    }
  }

  // ── Resolve Dizee-sourced artist name on mount ──
  // Checks multiple sources: localStorage dizeeUser, existing shows, and the profile API.
  useEffect(() => {
    // Helper: set once, skip if already resolved
    let resolved = false
    const apply = (name: string) => {
      const trimmed = (name ?? '').toString().trim()
      if (!trimmed || resolved) return
      resolved = true
      setDizeeArtistName(trimmed)
      setEditData((prev) => ({ ...prev, artist: prev.artist || trimmed }))
      // Persist to localStorage so future sessions have it immediately
      try {
        const raw = localStorage.getItem('dizeeUser')
        if (raw) {
          const stored = JSON.parse(raw)
          if (!stored?.dizeeArtistName) {
            localStorage.setItem('dizeeUser', JSON.stringify({ ...stored, dizeeArtistName: trimmed }))
          }
        }
      } catch { /* ignore */ }
    }

    // Source 1: dizeeUser in localStorage (fields: dizeeArtistName, artistName, name)
    try {
      const raw = localStorage.getItem('dizeeUser')
      if (raw) {
        const u = JSON.parse(raw)
        apply(u?.dizeeArtistName ?? u?.artistName ?? u?.artist_name ?? '')
      }
    } catch { /* ignore */ }

    // Source 2: existing shows already loaded (or cached) — most shows carry the artist name
    if (!resolved) {
      try {
        const cached = localStorage.getItem(showsCacheKey)
        if (cached) {
          const parsed = JSON.parse(cached)
          const list: any[] = parsed?.shows ?? []
          const first = list.find((s: any) => s.artist && typeof s.artist === 'string' && s.artist.trim())
          if (first) apply(first.artist)
        }
      } catch { /* ignore */ }
    }

    // Source 3: profile API — the backend knows the artist name from onboarding
    if (!resolved) {
      getDataAPI('user/getProfile')
        .then((response: any) => {
          const data = response?.data ?? response ?? {}
          const user = data?.user ?? data
          const apiName =
            data?.dizeeArtistName ?? user?.dizeeArtistName ??
            data?.artistName ?? data?.artist_name ??
            user?.artistName ?? user?.artist_name ??
            user?.name ?? ''
          apply(apiName)
        })
        .catch(() => {})
    }
  }, [])

  // When shows load, if we still don't have an artist name, grab it from the first show
  useEffect(() => {
    if (dizeeArtistName) return
    const first = shows.find((s) => s.artist && typeof s.artist === 'string' && s.artist.trim())
    if (first?.artist) {
      const name = first.artist.trim()
      setDizeeArtistName(name)
      setEditData((prev) => ({ ...prev, artist: prev.artist || name }))
      // Persist so it sticks across sessions
      try {
        const raw = localStorage.getItem('dizeeUser')
        if (raw) {
          const stored = JSON.parse(raw)
          if (!stored?.dizeeArtistName) {
            localStorage.setItem('dizeeUser', JSON.stringify({ ...stored, dizeeArtistName: name }))
          }
        }
      } catch { /* ignore */ }
    }
  }, [shows, dizeeArtistName])

  // Sync csvArtistOverride whenever dizeeArtistName is resolved
  useEffect(() => {
    if (dizeeArtistName && !csvArtistOverride) {
      setCsvArtistOverride(dizeeArtistName)
    }
  }, [dizeeArtistName]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const cached = readCachedShows()
    if (cached && cached.length) {
      setShows(cached)
      setIsLoading(false)
    }

    const fetchShows = async () => {
      try {
        const data = await getDataAPI('user/getBookings')
        setShows(normalizeBookings(data))
      } catch (error) {
        console.error('Error fetching shows:', error)
      } finally {
        setIsLoading(false)
      }
    }
    fetchShows()
  }, [])

  useEffect(() => {
    if (isLoading) return
    if (!shows.length) {
      // Clear cache when all shows are deleted so stale data doesn't flash on next visit
      try { window.localStorage.removeItem(showsCacheKey) } catch { /* ignore */ }
      // Also clear session cache used by OverviewPage / SchedulePage
      try { sessionStorage.removeItem(makeCacheKey([getEffectiveUserId() || 'self', 'api', 'user/getBookings'])) } catch { /* ignore */ }
      return
    }
    writeCachedShows(shows)
  }, [shows, isLoading])

  // ── Check for pending Bandsintown shows that need user approval ──
  useEffect(() => {
    if (isLoading) return
    try {
      const raw = localStorage.getItem('pendingBandsintownShows')
      if (!raw) return
      const pending = JSON.parse(raw)
      if (Array.isArray(pending) && pending.length > 0) {
        setPendingBITShows(pending)
        // Pre-select all for convenience
        setPendingBITApproved(new Set(pending.map((_: any, i: number) => i)))
        setShowBITApprovalModal(true)
      }
    } catch { /* ignore corrupt data */ }
  }, [isLoading])

  const handleBITApproveSelected = async () => {
    const approved = pendingBITShows.filter((_, i) => pendingBITApproved.has(i))
    if (!approved.length) {
      localStorage.removeItem('pendingBandsintownShows')
      setShowBITApprovalModal(false)
      setPendingBITShows([])
      return
    }
    setBitApprovalSaving(true)
    try {
      const toImport = dizeeArtistName
        ? approved.map((ev) => ({ ...ev, artist: dizeeArtistName }))
        : approved

      const latestShows = await fetchLatestShows()
      const { dupes, fresh } = detectDuplicates(toImport, latestShows)

      if (dupes.length > 0) {
        setDuplicateRows(dupes)
        setNonDuplicateRows(fresh)
        setDuplicateMergeSet(new Set())
        setShowDuplicateModal(true)
        localStorage.removeItem('pendingBandsintownShows')
        setShowBITApprovalModal(false)
        setPendingBITShows([])
        setBitApprovalSaving(false)
        return
      }

      await importRows(fresh)
    } catch (error) {
      console.error('Failed to save approved Bandsintown shows:', error)
    } finally {
      setBitApprovalSaving(false)
      localStorage.removeItem('pendingBandsintownShows')
      setShowBITApprovalModal(false)
      setPendingBITShows([])
      await refresh().catch(() => {})
    }
  }

  const handleBITDismiss = () => {
    localStorage.removeItem('pendingBandsintownShows')
    setShowBITApprovalModal(false)
    setPendingBITShows([])
  }

  const toggleBITApproval = (idx: number) => {
    setPendingBITApproved((prev) => {
      const next = new Set(prev)
      if (next.has(idx)) next.delete(idx)
      else next.add(idx)
      return next
    })
  }

  const toggleBITApproveAll = () => {
    if (pendingBITApproved.size === pendingBITShows.length) {
      setPendingBITApproved(new Set())
    } else {
      setPendingBITApproved(new Set(pendingBITShows.map((_, i) => i)))
    }
  }

  useEffect(() => {
    const counts: Record<string, string> = {}
    const percentages: Record<string, string> = {}
    const caps: Record<string, string> = {}
    shows.forEach((show) => {
      const id = show.id
      if (!id) return
      counts[id] = String(getTicketCount(show) || '')
      if (show.tix_sold_percent !== undefined && show.tix_sold_percent !== null) {
        percentages[id] = String(show.tix_sold_percent)
      }
      const raw = show.raw ?? {}
      const capVal = show.sellable_cap ?? raw.sellable_cap ?? raw.capacity ?? raw.cap ?? raw.venue_capacity
      if (capVal !== undefined && capVal !== null && capVal !== '') {
        caps[id] = String(capVal)
      }
    })
    setTicketCounts(counts)
    setTicketPercentages(percentages)
    setSellableCaps(caps)
    setTicketDirty(new Set())
  }, [shows])

  // Auto-calculate % tix sold from sellable cap and ticket count when either changes (manual form only)
  useEffect(() => {
    if (!isAddOpen || addView !== 'manual') return
    const cap = Number(editData.sellable_cap)
    const count =
      Number(editData.tix_sold) ||
      0
    if (cap > 0 && count >= 0) {
      const percent = Math.round((count / cap) * 100)
      setEditData((prev) => ({ ...prev, tix_sold_percent: percent }))
    }
  }, [isAddOpen, addView, editData.sellable_cap, editData.tix_sold])

  // Auto-disable ticket tracking when event title or venue contains "festival" (case-insensitive)
  useEffect(() => {
    if (!isAddOpen || addView !== 'manual') return
    const title = String(editData.event_title || '').toLowerCase()
    const venue = String(editData.venue || '').toLowerCase()
    if (title.includes('festival') || venue.includes('festival')) {
      setEditData((prev) => (prev.ticket_tracking === false ? prev : { ...prev, ticket_tracking: false }))
    }
  }, [isAddOpen, addView, editData.event_title, editData.venue])

  // When opening Bandsintown view: load saved connection and/or artist from profile (artist name not required from user)
  useEffect(() => {
    if (addView !== 'bandsintown') return
    const conn = getBandsintownConnection()
    if (conn) {
      setBandsintownAppId(conn.appId)
      if (conn.artist) setBandsintownArtist(conn.artist)
    }
    // Prefer Dizee source-of-truth artist name
    if (!conn?.artist && dizeeArtistName) {
      setBandsintownArtist(dizeeArtistName)
    } else if (!conn?.artist) {
      getDataAPI('user/getProfile')
        .then((response: any) => {
          const data = response?.data ?? response ?? {}
          const user = data?.user ?? data
          const artistName =
            data?.dizeeArtistName ??
            user?.dizeeArtistName ??
            data?.artistName ??
            data?.artist_name ??
            user?.artistName ??
            user?.artist_name ??
            user?.name ??
            ''
          if (artistName && typeof artistName === 'string') {
            setBandsintownArtist((prev) => prev || String(artistName).trim())
          }
        })
        .catch(() => {})
    }
  }, [addView, dizeeArtistName])

  // Check for edit item from navigation state
  useEffect(() => {
    const state = location.state as any
    if (state?.editItemId && state?.source === 'show' && shows.length > 0) {
      const show = shows.find((s) => s.id === state.editItemId)
      if (show) {
        startEditShow(show)
        // Clear the state to prevent re-triggering
        window.history.replaceState({}, document.title)
      }
    }
  }, [location.state, shows])

  const refresh = async () => {
    const data = await getDataAPI('user/getBookings').catch(() => [])
    setShows(normalizeBookings(data))
  }

  /**
   * Normalise multi-line cell values produced by CSV / Excel imports.
   * Cells that contain line-breaks (e.g. Alt+Enter in Excel, or \n inside a
   * quoted CSV field) would otherwise be silently concatenated when rendered.
   * We replace every internal line-break sequence with ", " so that
   * "INFO\nINFO2" becomes "INFO, INFO2".
   */
  const normalizeRowLineBreaks = (rows: string[][]): string[][] =>
    rows.map((row) =>
      row.map((cell) =>
        typeof cell === 'string'
          ? cell.replace(/\r\n|\r|\n/g, ', ').replace(/,\s*,/g, ',').trim()
          : cell,
      ),
    )

  const parseCsvText = (text: string) => {
    const rows: string[][] = []
    let current = ''
    let row: string[] = []
    let inQuotes = false

    for (let i = 0; i < text.length; i += 1) {
      const char = text[i]
      if (char === '"') {
        if (inQuotes && text[i + 1] === '"') {
          current += '"'
          i += 1
        } else {
          inQuotes = !inQuotes
        }
        continue
      }

      if (char === ',' && !inQuotes) {
        row.push(current)
        current = ''
        continue
      }

      if ((char === '\n' || char === '\r') && !inQuotes) {
        if (char === '\r' && text[i + 1] === '\n') {
          i += 1
        }
        row.push(current)
        if (row.some((value) => value.trim() !== '')) {
          rows.push(row)
        }
        row = []
        current = ''
        continue
      }

      current += char
    }

    if (current.length || row.length) {
      row.push(current)
      if (row.some((value) => value.trim() !== '')) {
        rows.push(row)
      }
    }

    return rows
  }

  const handleCsvFileChange = async (file: File) => {
    setCsvError(null)
    setCsvLoading(true)
    try {
      const fileName = file.name.toLowerCase()
      const isExcel = fileName.endsWith('.xlsx') || fileName.endsWith('.xls')

      let rows: string[][]

      if (isExcel) {
        // Parse Excel (.xlsx / .xls) via SheetJS
        const arrayBuffer = await file.arrayBuffer()
        const workbook = XLSX.read(arrayBuffer, { type: 'array' })
        const sheetName = workbook.SheetNames[0]
        if (!sheetName) {
          setCsvError('No sheets found in the Excel file.')
          setCsvRows([])
          setCsvFileName(file.name)
          return
        }
        const sheet = workbook.Sheets[sheetName]
        // Convert to array-of-arrays with raw values (dates become strings)
        rows = XLSX.utils.sheet_to_json<string[]>(sheet, {
          header: 1,
          raw: false,
          defval: '',
        }).filter((row: string[]) => row.some((cell) => String(cell ?? '').trim() !== ''))
      } else {
        // CSV
        const text = await file.text()
        rows = parseCsvText(text)
      }

      // Normalise multi-line cell values so "INFO\nINFO2" → "INFO, INFO2"
      rows = normalizeRowLineBreaks(rows)

      if (!rows.length) {
        setCsvError(isExcel ? 'No rows found in the Excel file.' : 'No rows found in CSV.')
        setCsvRows([])
        return
      }

      const headers = rows[0].map((value) => value.trim())
      const normalizeHeader = (header: string) =>
        header
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, ' ')
          .trim()
      // Reuse the comprehensive top-level city→state map for CSV/Excel imports
      const cityStateData = CITY_STATE_MAP

      const titleCaseCity = (input: string) =>
        input
          .trim()
          .split(/\s+/)
          .map((w) => (w.length ? w[0].toUpperCase() + w.slice(1).toLowerCase() : w))
          .join(' ')

      const lookupStateFromCity = (cityRaw: string): string | undefined => {
        const city = cityRaw.trim()
        if (!city) return undefined
        if (cityStateData[city]) return cityStateData[city]
        const tc = titleCaseCity(city)
        if (cityStateData[tc]) return cityStateData[tc]
        return undefined
      }

      const normalizeStatus = (raw: string): string => {
        const v = raw.trim().toLowerCase()
        if (!v) return 'pending'
        if (v.includes('confirm') || v.includes('published') || v.includes('reconcil') || v.includes('finals')) return 'confirmed'
        if (v.includes('pending') || v.includes('draft') || v.includes('hold')) return 'pending'
        if (v.includes('announce')) return 'announced'
        if (v.includes('private')) return 'private'
        if (v.includes('cancel')) return 'canceled'
        // PXS / agent exports sometimes have codes like DNA; treat as pending by default
        return 'pending'
      }
      const splitCityState = (raw: string): { city?: string; state?: string } => {
        // Strip trailing delimiters and whitespace (handles "Tokyo, " or "Bangkok,")
        const value = raw.trim().replace(/[,\-/]+\s*$/, '').trim()
        if (!value) return {}

        // Common delimiters from legacy + additional slash support
        const trySplit = (delimiter: string) => {
          const parts = value.split(delimiter).map((p) => p.trim()).filter(Boolean)
          if (parts.length >= 2) return { city: parts[0], state: parts[1] }
          return null
        }

        const comma = trySplit(',')
        if (comma) return comma
        const dash = value.includes(' - ') ? trySplit(' - ') : null
        if (dash) return dash
        const slash = value.includes('/') ? trySplit('/') : null
        if (slash) return slash

        // Handle "City ST" (e.g. "Austin TX")
        const m = value.match(/^(.*\S)\s+([A-Za-z]{2,3})$/)
        if (m) {
          const city = m[1].trim()
          const state = m[2].toUpperCase()
          return { city, state }
        }

        // If we can't confidently split, treat as city-only
        return { city: value }
      }

      const postProcessCityState = (data: Array<Record<string, any>>) => {
        return data.map((row) => {
          const next = { ...row }

          // --- Handle explicit city_state mapped column ---
          const cityStateRaw = typeof next.city_state === 'string' ? next.city_state : undefined
          if (cityStateRaw) {
            const hasCity = typeof next.city === 'string' && next.city.trim().length > 0
            const hasState = typeof next.state === 'string' && next.state.trim().length > 0
            if (!hasCity || !hasState) {
              const parsed = splitCityState(cityStateRaw)
              if (!hasCity && parsed.city) next.city = parsed.city
              if (!hasState && parsed.state) next.state = parsed.state
              if (
                (!hasState && (typeof next.state !== 'string' || !next.state.trim())) &&
                typeof next.city === 'string' &&
                next.city.trim()
              ) {
                const inferred = lookupStateFromCity(next.city)
                if (inferred) next.state = inferred
              }
            }
            // Match legacy: remove intermediate field so backend doesn't see unknown key
            delete next.city_state
          }

          // --- Handle "city" column that actually contains combined "City, State" ---
          // People often have a single "City" column with values like "Austin, TX"
          // or "Nashville, TN". If city contains a delimiter and state is empty, split it.
          if (
            typeof next.city === 'string' &&
            next.city.trim() &&
            (typeof next.state !== 'string' || !next.state.trim())
          ) {
            const cityVal = next.city.trim()
            // Check for common delimiters indicating a combined value
            const looksLikeCombined =
              cityVal.includes(',') ||
              cityVal.includes(' - ') ||
              /^.+\s+[A-Z]{2,3}$/.test(cityVal)

            if (looksLikeCombined) {
              const parsed = splitCityState(cityVal)
              if (parsed.city && parsed.state) {
                next.city = parsed.city
                next.state = parsed.state
              }
            }
          }

          // Legacy behavior: if city exists but state doesn't, try to infer from lookup
          if (
            typeof next.city === 'string' &&
            next.city.trim() &&
            (typeof next.state !== 'string' || !next.state.trim())
          ) {
            const inferred = lookupStateFromCity(next.city)
            if (inferred) next.state = inferred
          }

          // --- If city is still empty, try to extract location from venue name ---
          // Handles cases like "Private Residence - Weston, MA" or "Various Locations - Asia"
          if (typeof next.city !== 'string' || !next.city.trim()) {
            const venue = typeof next.venue === 'string' ? next.venue.trim() : ''
            if (venue) {
              // Try extracting location after a dash: "Private Residence - Weston, MA" → "Weston, MA"
              const dashMatch = venue.match(/\s[-–—]\s+(.+)$/)
              if (dashMatch) {
                const locationPart = dashMatch[1].trim()
                const parsed = splitCityState(locationPart)
                if (parsed.city) {
                  next.city = parsed.city
                  if (parsed.state) next.state = parsed.state
                  // Try lookup if state still missing
                  if (!next.state || !String(next.state).trim()) {
                    const inferred = lookupStateFromCity(next.city)
                    if (inferred) next.state = inferred
                  }
                }
              }
              // If still no city, use venue name itself as the city
              if (typeof next.city !== 'string' || !next.city.trim()) {
                next.city = venue
              }
            }
          }

          // Final fallback: if state is still empty, use the city name as
          // the state/province so the backend doesn't reject the row.
          // This is reasonable for international city-states (Singapore,
          // Hong Kong, Tokyo) and prevents import failures.
          if (
            typeof next.city === 'string' &&
            next.city.trim() &&
            (typeof next.state !== 'string' || !next.state.trim())
          ) {
            next.state = next.city.trim()
          }

          // Absolute last resort: if both are still empty, set to TBD
          if (typeof next.city !== 'string' || !next.city.trim()) {
            next.city = 'TBD'
          }
          if (typeof next.state !== 'string' || !next.state.trim()) {
            next.state = 'TBD'
          }

          return next
        })
      }
      const headerMap: Record<string, string> = {
        'perf date': 'perf_date',
        'performance date': 'perf_date',
        'show date': 'perf_date',
        date: 'perf_date',
        'event date': 'perf_date',
        artist: 'artist',
        'city state': 'city_state',
        'city st': 'city_state',
        'city province': 'city_state',
        'city region': 'city_state',
        location: 'city_state',
        'city state province': 'city_state',
        city: 'city',
        state: 'state',
        'state province': 'state',
        province: 'state',
        region: 'state',
        venue: 'venue',
        'event title': 'event_title',
        status: 'status',
        'set time': 'show_time',
        'show time': 'show_time',
        announce: 'announce_date',
        'announce date': 'announce_date',
        'on sale': 'on_sale_date',
        'on sale date': 'on_sale_date',
        guarantee: 'guarantee',
        walkout: 'walkout_potential',
        'walkout potential': 'walkout_potential',
        'sellable cap': 'sellable_cap',
        capacity: 'sellable_cap',
        promoter: 'promoter',
        '% tix sold': 'tix_sold_percent',
        'tix sold': 'tix_sold',
        'current ticket count': 'tix_sold',
        'ticket link': 'ticket_link',
        'appearing with': 'appearing_with',
        'deal info': 'deal_info',
        'artist net': 'artist_net',
        schedule: 'schedule',
        notes: 'notes',
      }

      const mappedRows = rows.slice(1).map((row) => {
        const entry: Record<string, any> = {}
        headers.forEach((header, index) => {
          const key = headerMap[normalizeHeader(header)]
          if (!key) return
          const value = row[index]?.trim()
          if (value !== undefined && value !== '') {
            if (key === 'status') {
              entry[key] = normalizeStatus(value)
              return
            }
            if (key.endsWith('_date') || key === 'perf_date') {
              entry[key] = normalizeLooseDate(value) || value
              return
            }
            entry[key] = value
          }
        })
        return entry
      })

      const cleanedRows = postProcessCityState(
        mappedRows.filter((row) => row.perf_date || row.venue || row.city || row.artist)
      )

      const finalRows = cleanedRows.filter(
        (row) => row.perf_date || row.venue || row.city || row.artist
      )

      if (!finalRows.length) {
        setCsvError('File parsed, but no valid rows matched known fields. Check that your column headers match expected names (e.g., Date, Artist, City, Venue).')
        setCsvRows([])
        return
      }

      setCsvFileName(file.name)
      setCsvRows(finalRows)
    } catch (error) {
      console.error('File parse failed:', error)
      setCsvError('Failed to parse file. Make sure it is a valid CSV or Excel file.')
      setCsvRows([])
    } finally {
      setCsvLoading(false)
    }
  }

  // ── Duplicate detection & import helpers ──

  /** Normalise a venue string for comparison (lowercase, trim, strip non-alphanumeric). */
  const normVenue = (v?: string) => (v ?? '').toLowerCase().replace(/[^a-z0-9]/g, '')

  /** Normalise a date string for comparison (YYYY-MM-DD). */
  const normDate = (d?: string) => normalizeLooseDate(String(d ?? '')) ?? ''

  /** Build a dedup key for a show (date + venue). */
  const showKey = (dateRaw?: string, venueRaw?: string) => {
    const d = normDate(dateRaw)
    const v = normVenue(venueRaw)
    return (d || v) ? `${d}||${v}` : ''
  }

  /**
   * Fetch the freshest shows from the server so duplicate detection never relies
   * on potentially-stale React state.
   */
  const fetchLatestShows = async (): Promise<Show[]> => {
    try {
      const data = await getDataAPI('user/getBookings')
      const normalized = normalizeBookings(data)
      setShows(normalized)
      return normalized
    } catch {
      return shows // fall back to current state
    }
  }

  /**
   * Compare incoming rows against the latest shows.
   * Returns { dupes, fresh } where `fresh` also has intra-batch dedup applied.
   */
  const detectDuplicates = (incoming: Record<string, any>[], latestShows: Show[]) => {
    // Build a Set of existing keys for O(1) lookup
    const existingKeys = new Map<string, Show>()
    for (const s of latestShows) {
      const k = showKey(s.perf_date || s.date, s.venue)
      if (k) existingKeys.set(k, s)
    }

    const dupes: Array<{ incoming: Record<string, any>; existing: Show }> = []
    const fresh: Record<string, any>[] = []
    const seenKeys = new Set<string>()

    for (const row of incoming) {
      const k = showKey(row.perf_date || row.date, row.venue)
      if (!k) { fresh.push(row); continue }

      const existingMatch = existingKeys.get(k)
      if (existingMatch) {
        dupes.push({ incoming: row, existing: existingMatch })
      } else if (seenKeys.has(k)) {
        // intra-batch duplicate — silently drop
        continue
      } else {
        seenKeys.add(k)
        fresh.push(row)
      }
    }
    return { dupes, fresh }
  }

  /**
   * Core import: sends the full batch in one API call.
   * The backend now handles duplicate detection (date+venue per user) and
   * skips Google Sheets sync when not configured, so no frontend workarounds needed.
   */
  const importRows = async (rows: Record<string, any>[]) => {
    if (!rows.length) return
    await postDataAPI('user/saveBookings', { data: rows })
  }

  /**
   * Called after the user resolves the duplicate modal.
   * Accepts an explicit `mergeIndices` set so we don't depend on React state
   * that may be stale (avoids the closure bug with "Skip all duplicates").
   */
  const handleDuplicateResolve = async (mergeIndices?: Set<number>) => {
    setDuplicateImporting(true)
    try {
      const effectiveMerge = mergeIndices ?? duplicateMergeSet
      const rowsToImport = [...nonDuplicateRows]
      for (const idx of effectiveMerge) {
        const entry = duplicateRows[idx]
        if (entry) {
          rowsToImport.push({ ...entry.incoming, _existingId: entry.existing.id })
        }
      }
      if (rowsToImport.length) {
        await importRows(rowsToImport)
      }
    } catch (error) {
      console.error('Import after duplicate resolution failed:', error)
    } finally {
      // ALWAYS close modals, reset state, refresh — no matter what happened
      setDuplicateImporting(false)
      setShowDuplicateModal(false)
      setDuplicateRows([])
      setNonDuplicateRows([])
      setDuplicateMergeSet(new Set())
      setIsAddOpen(false)
      resetAddModal()
      await refresh().catch(() => {})
    }
  }

  const handleCsvImport = async () => {
    if (!csvRows.length) return
    setCsvImporting(true)
    setCsvError(null)
    try {
      const artistName = csvArtistOverride.trim() || dizeeArtistName
      const rowsToImport = artistName
        ? csvRows.map((row) => ({ ...row, artist: artistName }))
        : csvRows

      // ── Duplicate detection (fetches fresh shows from server) ──
      const latestShows = await fetchLatestShows()
      const { dupes, fresh } = detectDuplicates(rowsToImport, latestShows)

      if (dupes.length > 0) {
        setDuplicateRows(dupes)
        setNonDuplicateRows(fresh)
        setDuplicateMergeSet(new Set())
        setShowDuplicateModal(true)
        setCsvImporting(false)
        return
      }

      await importRows(fresh)
    } catch (error) {
      console.error('CSV import failed:', error)
    } finally {
      // ALWAYS close + refresh no matter what
      setCsvImporting(false)
      setIsAddOpen(false)
      resetAddModal()
      await refresh().catch(() => {})
    }
  }

  const handleBandsintownPreview = async () => {
    if (!bandsintownAppId.trim()) return
    setBandsintownError(null)
    setBandsintownLoading(true)
    try {
      let artist = bandsintownArtist.trim()
      // Prefer the Dizee source-of-truth artist name
      if (!artist && dizeeArtistName) {
        artist = dizeeArtistName
        setBandsintownArtist(artist)
      }
      if (!artist) {
        const profile: any = await getDataAPI('user/getProfile').catch(() => ({}))
        const data = profile?.data ?? profile ?? {}
        const user = data?.user ?? data
        artist = (
          data?.dizeeArtistName ??
          user?.dizeeArtistName ??
          data?.artistName ??
          data?.artist_name ??
          user?.artistName ??
          user?.artist_name ??
          user?.name ??
          ''
        ).trim()
        if (artist) setBandsintownArtist(artist)
      }
      if (!artist) {
        setBandsintownError(
          "We need an artist to fetch events. Add your artist name in Settings (Profile), then try again."
        )
        setBandsintownLoading(false)
        return
      }

      const response = await fetch(
        `https://rest.bandsintown.com/artists/${encodeURIComponent(artist)}/events?app_id=${encodeURIComponent(bandsintownAppId.trim())}`
      )
      if (!response.ok) {
        throw new Error('Failed to fetch events')
      }
      const events = await response.json()
      if (!Array.isArray(events) || events.length === 0) {
        setBandsintownError('No upcoming events found for this artist.')
        setBandsintownEvents([])
        setBandsintownLoading(false)
        return
      }

      setBandsintownConnection(bandsintownAppId.trim(), artist)

      const bookings = events.map((event: any) => {
        // Use normalizeLooseDate → parseLocalDate pipeline to avoid off-by-one UTC issues
        const rawDatetime = event.datetime || ''
        const looseNormalized = normalizeLooseDate(rawDatetime)
        let perfDateKey: string
        let perfDate: Date
        if (looseNormalized) {
          perfDateKey = looseNormalized
          const m = looseNormalized.match(/^(\d{4})-(\d{2})-(\d{2})$/)
          perfDate = m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]), 12, 0, 0) : new Date(rawDatetime)
        } else {
          perfDate = new Date(rawDatetime)
          perfDateKey = toLocalDateKey(perfDate)
        }
        return {
          perf_date: perfDateKey,
          artist,
          venue: event.venue?.name,
          city: event.venue?.city,
          state: event.venue?.region || event.venue?.country,
          country: event.venue?.country,
          status: 'confirmed',
          ticket_link: event.url,
          promoter: event.venue?.name,
          bandsintown_id: event.id,
          source: 'bandsintown',
          venue_address: `${event.venue?.city || ''}, ${event.venue?.region || event.venue?.country || ''}`.trim(),
          show_time: perfDate.toLocaleTimeString('en-US', {
            hour: '2-digit',
            minute: '2-digit',
          }),
          event_description: event.description || '',
          appearing_with: Array.isArray(event.lineup) ? event.lineup.join(', ') : artist,
          event_title: event.title || event.venue?.name,
        }
      })

      setBandsintownEvents(bookings)
    } catch (error) {
      console.error('Bandsintown import failed:', error)
      setBandsintownError('Failed to fetch Bandsintown events. Please verify credentials.')
      setBandsintownEvents([])
    } finally {
      setBandsintownLoading(false)
    }
  }

  const handleBandsintownImport = async () => {
    if (!bandsintownEvents.length) return
    setBandsintownImporting(true)
    try {
      const eventsToImport = dizeeArtistName
        ? bandsintownEvents.map((ev) => ({ ...ev, artist: dizeeArtistName }))
        : bandsintownEvents

      const latestShows = await fetchLatestShows()
      const { dupes, fresh } = detectDuplicates(eventsToImport, latestShows)

      if (dupes.length > 0) {
        setDuplicateRows(dupes)
        setNonDuplicateRows(fresh)
        setDuplicateMergeSet(new Set())
        setShowDuplicateModal(true)
        setBandsintownImporting(false)
        return
      }

      await importRows(fresh)
      setBandsintownConnection(bandsintownAppId.trim(), bandsintownArtist.trim() || (bandsintownEvents[0]?.artist ?? ''))
    } catch (error) {
      console.error('Bandsintown import failed:', error)
    } finally {
      setBandsintownImporting(false)
      setIsAddOpen(false)
      resetAddModal()
      await refresh().catch(() => {})
    }
  }

  const handleTicketCountChange = (showId: string, value: string) => {
    setTicketCounts((prev) => ({ ...prev, [showId]: value }))
    setTicketDirty((prev) => new Set(prev).add(showId))
    // Auto-recalculate % sold when ticket count changes and sellable cap is known
    const cap = Number(sellableCaps[showId] || 0) || 0
    const count = Number(value) || 0
    if (cap > 0 && count > 0) {
      setTicketPercentages((prev) => ({
        ...prev,
        [showId]: String(Math.round((count / cap) * 100)),
      }))
    }
  }

  const handleTicketPercentageChange = (showId: string, value: string) => {
    setTicketPercentages((prev) => ({ ...prev, [showId]: value }))
    setTicketDirty((prev) => new Set(prev).add(showId))
  }

  const handleSellableCapChange = (showId: string, value: string) => {
    setSellableCaps((prev) => ({ ...prev, [showId]: value }))
    setTicketDirty((prev) => new Set(prev).add(showId))
    // Auto-recalculate % sold when sellable cap changes
    const count = Number(ticketCounts[showId] || 0) || 0
    const cap = Number(value) || 0
    if (cap > 0 && count > 0) {
      setTicketPercentages((prev) => ({
        ...prev,
        [showId]: String(Math.round((count / cap) * 100)),
      }))
    }
  }

  const toggleTicketTracking = async (show: Show) => {
    const showId = show.id
    if (!showId) return
    const newTracking = !isTrackingTickets(show)
    setTicketSaving((prev) => ({ ...prev, [showId]: true }))
    try {
      const raw = show.raw || {}
      const { current_ticket_count: _legacyCurrentTicketCount, ...rawWithoutLegacy } = raw as any
      const updateData = {
        ...rawWithoutLegacy,
        ticket_tracking: newTracking,
      }
      await putDataAPI('user/updateBooking', {
        bookingId: showId,
        data: updateData,
      })
      // Update the local shows state so the UI reflects the change immediately
      setShows((prev) =>
        prev.map((s) =>
          s.id === showId ? { ...s, ticket_tracking: newTracking, raw: { ...s.raw, ticket_tracking: newTracking } } : s
        )
      )
    } catch (error) {
      console.error('Toggle ticket tracking failed:', error)
      alert('Failed to update ticket tracking. Please try again.')
    } finally {
      setTicketSaving((prev) => ({ ...prev, [showId]: false }))
    }
  }

  const saveTicketForShow = async (show: Show) => {
    const showId = show.id
    if (!showId) return
    setTicketSaving((prev) => ({ ...prev, [showId]: true }))
    try {
      const raw = show.raw || {}
      // Don't keep writing legacy `current_ticket_count`; we only persist `tix_sold` now.
      // (We still send a couple of common aliases for backend compatibility.)
      const { current_ticket_count: _legacyCurrentTicketCount, ...rawWithoutLegacy } = raw as any
      const numericCount = Number(ticketCounts[showId] || 0) || 0
      // Use the edited sellable cap if available, otherwise fall back to existing value
      const editedCap = sellableCaps[showId]
      const capacity = editedCap !== undefined && editedCap !== ''
        ? Number(editedCap) || 0
        : Number(show.sellable_cap || raw.capacity || 0) || 0
      const manualPercentage = ticketPercentages[showId]
      const autoPercentage =
        capacity > 0 && numericCount > 0 ? Math.round((numericCount / capacity) * 100) : 0
      const percentageToSave =
        manualPercentage !== undefined && manualPercentage !== '' ? Number(manualPercentage) || 0 : autoPercentage

      const updateData = {
        ...rawWithoutLegacy,
        ticket_count: numericCount,
        tickets_sold: numericCount,
        tix_sold: numericCount,
        sellable_cap: capacity || undefined,
        capacity: capacity || undefined,
        percentage_sold: percentageToSave,
        tix_sold_percent: percentageToSave,
        last_ticket_count_date: new Date().toISOString(),
      }

      await putDataAPI('user/updateBooking', {
        bookingId: showId,
        data: updateData,
      })

      setTicketDirty((prev) => {
        const next = new Set(prev)
        next.delete(showId)
        return next
      })
      await refresh()
    } catch (error) {
      console.error('Ticket update failed:', error)
      alert('Failed to update ticket count. Please try again.')
    } finally {
      setTicketSaving((prev) => ({ ...prev, [showId]: false }))
    }
  }

  const saveAllTickets = async (showsToSave: Show[]) => {
    for (const show of showsToSave) {
      if (!ticketDirty.has(show.id)) continue
      if (!isTrackingTickets(show)) continue
      // eslint-disable-next-line no-await-in-loop
      await saveTicketForShow(show)
    }
  }

  // ── Ticket Import helpers ──────────────────────────────────────────────

  /** Fuzzy-match a label string to the best upcoming show. */
  const fuzzyMatchShow = (
    label: string,
    city?: string,
    date?: string,
  ): Show | null => {
    const lower = label.toLowerCase().trim()
    if (!lower && !city && !date) return null

    let bestMatch: Show | null = null
    let bestScore = 0

    for (const show of upcomingShows) {
      if (!isTrackingTickets(show)) continue
      let score = 0

      const showVenue = (show.venue || '').toLowerCase()
      const showTitle = (show.event_title || show.title || '').toLowerCase()
      const showCity = (show.city || '').toLowerCase()

      // Venue / title matching
      if (lower) {
        if (showVenue && lower === showVenue) score += 20
        else if (showTitle && lower === showTitle) score += 20
        else if (showVenue && (lower.includes(showVenue) || showVenue.includes(lower))) score += 10
        else if (showTitle && (lower.includes(showTitle) || showTitle.includes(lower))) score += 10
      }

      // City matching
      const cityLower = (city || '').toLowerCase().trim()
      if (cityLower && showCity) {
        if (cityLower === showCity) score += 5
        else if (showCity.includes(cityLower) || cityLower.includes(showCity)) score += 2
      }

      // Date matching
      if (date) {
        const normalizedImport = normalizeLooseDate(date)
        const normalizedShow = show.perf_date
        if (normalizedImport && normalizedShow && normalizedImport === normalizedShow) score += 15
      }

      if (score > bestScore) {
        bestScore = score
        bestMatch = show
      }
    }

    return bestScore >= 5 ? bestMatch : null
  }

  /** Parse free-form text into ticket entries. */
  const parseTicketText = (
    text: string,
  ): Array<{ label: string; count: number; percent?: number; city?: string; date?: string }> => {
    const results: Array<{ label: string; count: number; percent?: number; city?: string; date?: string }> = []
    const lines = text.split(/\n/).map((l) => l.trim()).filter(Boolean)

    for (const line of lines) {
      // Tab-separated: "Venue Name\tCity\t626\t78%"
      const tabs = line.split('\t')
      if (tabs.length >= 2) {
        let label = ''
        let city = ''
        let count: number | null = null
        let percent: number | undefined

        for (const cell of tabs) {
          const trimmed = cell.trim()
          if (count === null && /^[\d,]+$/.test(trimmed) && trimmed.length > 0) {
            count = parseInt(trimmed.replace(/,/g, ''), 10)
          } else if (count !== null && /^\d+%?$/.test(trimmed)) {
            percent = parseInt(trimmed.replace('%', ''), 10)
          } else if (count === null && trimmed && !/^\d/.test(trimmed)) {
            if (!label) label = trimmed
            else if (!city) city = trimmed
          }
        }

        if (label && count !== null) {
          results.push({ label, count, percent, city: city || undefined })
          continue
        }
      }

      // "Venue - 626 sold" or "Venue: 626" or "Venue – 1,200 (85%)"
      const dashMatch = line.match(
        /^(.+?)[\s]*[-–—:]\s*([\d,]+)\s*(?:sold|tickets?)?\s*(?:\(?\s*(\d+)\s*%?\s*\)?)?/i,
      )
      if (dashMatch) {
        results.push({
          label: dashMatch[1].trim(),
          count: parseInt(dashMatch[2].replace(/,/g, ''), 10),
          percent: dashMatch[3] ? parseInt(dashMatch[3], 10) : undefined,
        })
        continue
      }

      // "Venue 626 78%"
      const simpleMatch = line.match(/^(.+?)\s+([\d,]+)\s*(?:\(?\s*(\d+)\s*%?\s*\)?)?$/)
      if (simpleMatch && simpleMatch[1].trim().length > 2 && !/^\d/.test(simpleMatch[1].trim())) {
        results.push({
          label: simpleMatch[1].trim(),
          count: parseInt(simpleMatch[2].replace(/,/g, ''), 10),
          percent: simpleMatch[3] ? parseInt(simpleMatch[3], 10) : undefined,
        })
      }
    }

    return results
  }

  /** Parse CSV/Excel ticket data and match to upcoming shows. */
  const parseTicketFile = async (file: File) => {
    const fileName = file.name.toLowerCase()
    const isExcel = fileName.endsWith('.xlsx') || fileName.endsWith('.xls')
    const isCsv = fileName.endsWith('.csv')
    const isPdf = fileName.endsWith('.pdf')
    const isImage = /\.(png|jpe?g|gif|webp|bmp|tiff?)$/i.test(fileName)

    if (isPdf) {
      // Extract text from PDF using pdf.js
      const arrayBuffer = await file.arrayBuffer()
      const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise
      const textParts: string[] = []
      for (let i = 1; i <= pdf.numPages; i++) {
        // eslint-disable-next-line no-await-in-loop
        const page = await pdf.getPage(i)
        // eslint-disable-next-line no-await-in-loop
        const content = await page.getTextContent()
        const pageText = content.items
          .map((item: any) => ('str' in item ? item.str : ''))
          .join(' ')
        textParts.push(pageText)
      }
      return parseTicketText(textParts.join('\n'))
    }

    if (isImage) {
      throw new Error(
        'Image files can\'t be read directly. Please copy the text from the image and paste it in the text area instead.',
      )
    }

    if (!isExcel && !isCsv) {
      // Treat as plain text
      const text = await file.text()
      return parseTicketText(text)
    }

    // CSV / Excel
    let rows: string[][]

    if (isExcel) {
      const arrayBuffer = await file.arrayBuffer()
      const workbook = XLSX.read(arrayBuffer, { type: 'array' })
      const sheetName = workbook.SheetNames[0]
      if (!sheetName) throw new Error('No sheets found in the Excel file.')
      const sheet = workbook.Sheets[sheetName]
      rows = XLSX.utils
        .sheet_to_json<string[]>(sheet, { header: 1, raw: false, defval: '' })
        .filter((row: string[]) => row.some((cell) => String(cell ?? '').trim() !== ''))
    } else {
      const text = await file.text()
      rows = parseCsvText(text)
    }

    // Normalise multi-line cell values so "INFO\nINFO2" → "INFO, INFO2"
    rows = normalizeRowLineBreaks(rows)

    if (!rows.length) throw new Error('No rows found in the file.')

    const headers = rows[0].map((h) =>
      h.trim().toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim(),
    )

    const findCol = (...names: string[]) =>
      headers.findIndex((h) => names.some((n) => h.includes(n)))

    const venueCol = findCol('venue', 'event', 'show', 'title')
    const cityCol = findCol('city', 'location', 'market')
    const dateCol = findCol('date', 'perf')
    const countCol = findCol('sold', 'ticket', 'count', 'tix')
    const percentCol = findCol('percent', '% sold', 'pct')

    if (countCol === -1) {
      // Fallback: treat the whole file as text and parse
      const fullText = rows.map((r) => r.join('\t')).join('\n')
      return parseTicketText(fullText)
    }

    const results: Array<{
      label: string
      count: number
      percent?: number
      city?: string
      date?: string
    }> = []

    for (let i = 1; i < rows.length; i++) {
      const row = rows[i]
      const countRaw = String(row[countCol] || '').replace(/[^0-9]/g, '')
      const count = countRaw ? parseInt(countRaw, 10) : 0
      if (!count) continue

      const label = venueCol >= 0 ? String(row[venueCol] || '').trim() : ''
      const city = cityCol >= 0 ? String(row[cityCol] || '').trim() : ''
      const date = dateCol >= 0 ? String(row[dateCol] || '').trim() : ''
      const percentRaw =
        percentCol >= 0 ? String(row[percentCol] || '').replace(/[^0-9]/g, '') : ''
      const percent = percentRaw ? parseInt(percentRaw, 10) : undefined

      results.push({ label: label || city || date, count, percent, date, city })
    }

    return results
  }

  /** Run the import pipeline: parse → match → show results. */
  const runTicketImport = async (source: 'file' | 'text', file?: File) => {
    setTicketImportParsing(true)
    setTicketImportError(null)
    setTicketImportMatches([])

    try {
      let entries: Array<{
        label: string
        count: number
        percent?: number
        city?: string
        date?: string
      }>

      if (source === 'file' && file) {
        entries = await parseTicketFile(file)
        setTicketImportFileName(file.name)
      } else {
        entries = parseTicketText(ticketImportText)
      }

      if (!entries.length) {
        setTicketImportError('No ticket data found. Make sure the data includes venue names and ticket counts.')
        return
      }

      // Match entries to upcoming shows
      const matches: Array<{ showId: string; showLabel: string; count: number; percent?: number }> =
        []
      const usedShowIds = new Set<string>()

      for (const entry of entries) {
        const show = fuzzyMatchShow(entry.label, entry.city, entry.date)
        if (show && !usedShowIds.has(show.id)) {
          usedShowIds.add(show.id)
          matches.push({
            showId: show.id,
            showLabel: show.event_title || show.venue || 'Untitled show',
            count: entry.count,
            percent: entry.percent,
          })
        }
      }

      if (!matches.length) {
        setTicketImportError(
          `Parsed ${entries.length} entries but couldn't match any to your upcoming shows. Check that the venue names or dates match.`,
        )
        return
      }

      setTicketImportMatches(matches)
    } catch (err: any) {
      setTicketImportError(err?.message || 'Failed to parse ticket data.')
    } finally {
      setTicketImportParsing(false)
    }
  }

  /** Apply matched ticket import results to the ticket counter fields. */
  const applyTicketImportMatches = () => {
    for (const match of ticketImportMatches) {
      setTicketCounts((prev) => ({ ...prev, [match.showId]: String(match.count) }))
      if (match.percent !== undefined) {
        setTicketPercentages((prev) => ({ ...prev, [match.showId]: String(match.percent) }))
      }
      setTicketDirty((prev) => new Set(prev).add(match.showId))
    }
    // Switch back to the regular ticket counter view
    setTicketImportMode(false)
    setTicketImportText('')
    setTicketImportFileName(null)
    setTicketImportMatches([])
    setTicketImportError(null)
  }

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsSubmitting(true)
    try {
      // For new shows, use Dizee artist name unless the user explicitly unlocked and edited it
      const effectiveArtist = (!isEditing && dizeeArtistName && !artistFieldUnlocked) ? dizeeArtistName : editData.artist
      const dataWithArtist: Record<string, any> = { ...editData, artist: effectiveArtist }

      const required = ['perf_date', 'artist', 'city', 'state', 'venue'] as const
      const missing = required.filter((k) => !String(dataWithArtist[k] ?? '').trim())
      if (missing.length) {
        alert(`Please fill required fields: ${missing.join(', ')}`)
        return
      }

      if (isEditing && editShowId) {
        const stateVal = String(dataWithArtist.state ?? '').trim()
        const cityVal = String(dataWithArtist.city ?? '').trim()
        const existingShow = shows.find((s) => s.id === editShowId)
        const existingRaw = existingShow?.raw ?? {}
        const { current_ticket_count: _legacyCurrentTicketCount, ...existingRawWithoutLegacy } = existingRaw as any
        const updatePayload = {
          ...existingRawWithoutLegacy,
          ...dataWithArtist,
          event_title: dataWithArtist.event_title || dataWithArtist.venue,
          perf_date: dataWithArtist.perf_date,
          city: cityVal,
          state: stateVal,
          state_province: stateVal,
        }
        await putDataAPI('user/updateBooking', {
          bookingId: editShowId,
          data: updatePayload,
        })
        // Persist ticket_tracking locally so it survives even if the backend doesn't return it
        const trackingVal = dataWithArtist.ticket_tracking
        writeTrackingOverride(editShowId, trackingVal !== false && trackingVal !== 'false' && trackingVal !== 0)
      } else {
        // Create new show — backend handles duplicate detection & sheets sync
        await postDataAPI('user/saveBookings', {
          data: {
            ...dataWithArtist,
            event_title: dataWithArtist.event_title || dataWithArtist.venue,
            perf_date: dataWithArtist.perf_date,
          },
        })
      }
      
      setIsAddOpen(false)
      resetAddModal()
      await refresh()
      // Save field values as suggestions for future autofill
      const keysToSuggest = ['artist', 'city', 'state', 'venue', 'event_title', 'promoter', 'guarantee']
      keysToSuggest.forEach((key) => {
        const v = editData[key]
        if (v !== undefined && v !== null && String(v).trim()) addShowFieldSuggestion(key, String(v).trim())
      })
    } catch (error) {
      console.error('Failed to save show:', error)
      alert('Failed to save show. Please check fields and try again.')
    } finally {
      setIsSubmitting(false)
    }
  }

  const startEditShow = (show: Show) => {
    const raw = show.raw || {}
    const tixSoldCandidate =
      show.tix_sold ??
      show.ticket_count ??
      raw.tix_sold ??
      raw.tickets_sold ??
      raw.ticket_count
    const tixSoldNumber =
      tixSoldCandidate === undefined || tixSoldCandidate === null || tixSoldCandidate === ''
        ? ''
        : (() => {
            const n = typeof tixSoldCandidate === 'number' ? tixSoldCandidate : Number(tixSoldCandidate)
            return Number.isNaN(n) ? '' : n
          })()
    setEditData({
      artist: show.artist || '',
      perf_date:
        normalizeLooseDate(String(show.perf_date || show.date || raw.perf_date || raw.date || '')) ||
        show.perf_date ||
        show.date ||
        toLocalDateKey(new Date()),
      city: show.city || '',
      state: show.state ?? raw.state ?? '',
      venue: show.venue || '',
      event_title: show.event_title || show.title || '',
      status: show.status || 'pending',
      show_time: raw.show_time || '',
      announce_date: raw.announce_date || '',
      on_sale_date: raw.on_sale_date || '',
      guarantee: raw.guarantee || '',
      walkout_potential: raw.walkout_potential || '',
      sellable_cap: (() => {
        const v = show.sellable_cap ?? raw.sellable_cap ?? raw.capacity ?? raw.cap ?? raw.venue_capacity
        if (v === undefined || v === null || v === '') return ''
        // Strip non-numeric chars (commas, $, spaces) so <input type="number"> can display it
        const n = String(v).replace(/[^0-9.\-]/g, '')
        return n || ''
      })(),
      promoter: raw.promoter || '',
      tix_sold_percent: show.tix_sold_percent || '',
      tix_sold: tixSoldNumber,
      appearing_with: raw.appearing_with || '',
      red_flag: raw.red_flag || false,
      red_flag_notes: raw.red_flag_notes || '',
      promoter_company: raw.promoter_company || '',
      promoter_contact_names: raw.promoter_contact_names || '',
      promoter_contact_emails: raw.promoter_contact_emails || '',
      venue_category: raw.venue_category || '',
      billing_type: raw.billing_type || '',
      territory: raw.territory || '',
      advanced_info: raw.advanced_info || '',
      deal_type: raw.deal_type || '',
      deal_info: raw.deal_info || '',
      gross_potential: raw.gross_potential || '',
      net_potential: raw.net_potential || '',
      artist_net: raw.artist_net || '',
      artist_profit: raw.artist_profit || '',
      split_point: raw.split_point || '',
      amount_to_split: raw.amount_to_split || '',
      ticket_tracking: show.ticket_tracking === false ? false : show.ticket_tracking === true ? true : (raw.ticket_tracking === false || raw.ticket_tracking === 'false') ? false : true,
      ticket_notes: raw.ticket_notes || '',
      ticket_prices_tiers: raw.ticket_prices_tiers || '',
      ticket_taxes_fees: raw.ticket_taxes_fees || '',
      total_fees: raw.total_fees || '',
      deposit_due: raw.deposit_due || '',
      deposit_received: raw.deposit_received || '',
      contract_status: raw.contract_status || '',
      currency_conversion: raw.currency_conversion || '',
      additional_clauses: raw.additional_clauses || '',
      schedule: raw.schedule || '',
      notes: raw.notes || '',
    })
    setIsEditing(true)
    setEditShowId(show.id)
    setAddView('manual')
    setIsAddOpen(true)
  }

  const handleDeleteShow = async (showId: string) => {
    if (!window.confirm('Are you sure you want to delete this show?')) return

    // Snapshot for rollback
    const prev = shows

    // Optimistically remove the show from the UI immediately
    setShows((s) => s.filter((x) => x.id !== showId))
    setSelectedShowIds((s) => { const n = new Set(s); n.delete(showId); return n })
    setOpenDropdown(null)
    setDropdownPosition(null)

    try {
      await deleteDataAPI(`user/deleteBooking/${showId}`)
    } catch (error) {
      console.error('Failed to delete show:', error)
      alert('Failed to delete show. Please try again.')
      // Revert optimistic removal so the show reappears
      setShows(prev)
    }
  }

  const showStatusOptions = ['pending', 'confirmed', 'announced', 'private', 'canceled'] as const
  type ShowStatus = (typeof showStatusOptions)[number]

  const handleShowStatusChange = async (showId: string, newStatus: ShowStatus) => {
    if (!showId) {
      alert('Cannot update status: missing show id.')
      return
    }
    const target = shows.find((s) => s.id === showId)
    if (!target) {
      alert('Cannot update status: show not found.')
      return
    }
    const prevStatus = (target.status || 'pending') as ShowStatus
    // Optimistic UI: immediately reflect the status change.
    setShows((prev) =>
      prev.map((s) => (s.id === showId ? { ...s, status: newStatus, raw: { ...(s.raw || {}), status: newStatus } } : s))
    )
    setStatusSaving((prev) => ({ ...prev, [showId]: true }))
    const setSuccess = () => {
      // Already applied optimistically; keep it.
      setOpenDropdown(null)
      setDropdownPosition(null)
    }
    try {
      const raw = target.raw || {}
      const { current_ticket_count: _legacyCurrentTicketCount, ...rawWithoutLegacy } = raw as any

      const perf_date = (target.perf_date || target.date || raw.perf_date || raw.date || '').trim?.() ?? (target.perf_date || target.date || raw.perf_date || raw.date || '')
      const artist =
        (target.artist || raw.artist || raw.artist_name || '').trim?.() ??
        (target.artist || raw.artist || raw.artist_name || '')
      const venue = (target.venue || raw.venue || '').trim?.() ?? (target.venue || raw.venue || '')
      const city = (target.city || raw.city || '').trim?.() ?? (target.city || raw.city || '')
      const state =
        (target.state || raw.state || raw.state_province || '').trim?.() ??
        (target.state || raw.state || raw.state_province || '')
      const event_title =
        (target.event_title || raw.event_title || raw.title || target.title || venue || '').trim?.() ??
        (target.event_title || raw.event_title || raw.title || target.title || venue || '')

      const updatePayload = {
        ...rawWithoutLegacy,
        // Ensure required fields are present for backends that validate full booking objects.
        perf_date,
        city,
        venue,
        artist,
        event_title,
        state,
        state_province: state,
        status: newStatus,
      }

      // Prefer body form with bookingId (used by EditBooking, TicketCounter).
      await putDataAPI('user/updateBooking', { bookingId: showId, data: updatePayload })
      setSuccess()
    } catch (err: any) {
      console.error('Failed to update status:', err?.response?.data ?? err)
      const msg =
        err?.response?.data?.message ??
        err?.response?.data?.error ??
        (typeof err?.response?.data === 'string' ? err.response.data : null) ??
        err?.message
      // Roll back optimistic update.
      setShows((prev) =>
        prev.map((s) => (s.id === showId ? { ...s, status: prevStatus, raw: { ...(s.raw || {}), status: prevStatus } } : s))
      )
      alert(msg ? `Failed to update status: ${msg}` : 'Failed to update status. Please try again.')
    } finally {
      setStatusSaving((prev) => ({ ...prev, [showId]: false }))
    }
  }

  const toggleShowSelection = (showId: string) => {
    setSelectedShowIds((prev) => {
      const next = new Set(prev)
      if (next.has(showId)) next.delete(showId)
      else next.add(showId)
      return next
    })
  }

  const selectAllFiltered = () => {
    const searchLower = searchQuery.trim().toLowerCase()
    const filtered = shows.filter((show) => {
      if (statusFilter && (show.status || '') !== statusFilter) return false
      if (!searchLower) return true
      const title = (show.event_title || show.title || show.venue || '').toLowerCase()
      const venue = (show.venue || '').toLowerCase()
      const city = (show.city || '').toLowerCase()
      const artist = (show.artist || '').toLowerCase()
      return title.includes(searchLower) || venue.includes(searchLower) || city.includes(searchLower) || artist.includes(searchLower)
    })
    const ids = filtered.map((s) => s.id)
    if (ids.length === 0) return
    setSelectedShowIds((prev) => {
      const allSelected = ids.every((id) => prev.has(id))
      if (allSelected) {
        const next = new Set(prev)
        ids.forEach((id) => next.delete(id))
        return next
      }
      const next = new Set(prev)
      ids.forEach((id) => next.add(id))
      return next
    })
  }

  const handleBulkDelete = async () => {
    if (selectedShowIds.size === 0) return
    if (!window.confirm(`Delete ${selectedShowIds.size} selected show${selectedShowIds.size === 1 ? '' : 's'}? This cannot be undone.`)) return
    setIsBulkDeleting(true)

    // Snapshot the IDs to delete and the current state (for rollback)
    const idsToDelete = new Set(selectedShowIds)
    const prevShows = shows

    // Optimistically remove all selected shows from the UI immediately
    setShows((prev) => prev.filter((s) => !idsToDelete.has(s.id)))
    setSelectedShowIds(new Set())
    setOpenDropdown(null)
    setDropdownPosition(null)

    try {
      const result = await postDataAPI('user/bulkDeleteBookings', {
        ids: Array.from(idsToDelete),
      })

      const deletedCount = result?.data?.deletedCount ?? idsToDelete.size
      const requestedCount = result?.data?.requestedCount ?? idsToDelete.size
      if (deletedCount < requestedCount) {
        alert(`Deleted ${deletedCount} of ${requestedCount} show(s). Some may have already been removed.`)
      }
    } catch (error) {
      console.error('Bulk delete failed:', error)
      alert('Failed to delete shows. Please try again.')
      // Revert optimistic removal so the shows reappear
      setShows(prevShows)
    } finally {
      setIsBulkDeleting(false)
    }
  }

  const today = new Date()
  today.setHours(0, 0, 0, 0)

  // Current week (Sun–Sat) for "this week" stats
  const dayOfWeek = today.getDay()
  const weekStart = new Date(today)
  weekStart.setDate(today.getDate() - dayOfWeek)
  weekStart.setHours(0, 0, 0, 0)
  const weekEnd = new Date(weekStart)
  weekEnd.setDate(weekStart.getDate() + 6)
  weekEnd.setHours(23, 59, 59, 999)

  const getResolvedTicketCount = (show: Show) => {
    const edited = ticketCounts[show.id]
    if (edited !== undefined && edited !== '') {
      const n = Number(edited)
      if (!Number.isNaN(n)) return n
    }
    return getTicketCount(show)
  }

  const getCapacity = (show: Show) => {
    const raw = show.raw ?? {}
    const val =
      show.sellable_cap ??
      raw.sellable_cap ??
      raw.capacity ??
      raw.cap ??
      raw.venue_capacity
    if (val === undefined || val === null || val === '') return 0
    // Accept values like "1,200" or "$1,200" (keeps behavior consistent across app).
    const n =
      typeof val === 'number'
        ? val
        : Number(String(val).replace(/[^0-9.-]/g, ''))
    return Number.isNaN(n) ? 0 : Math.max(0, n)
  }

  // Rolling last 7 days (inclusive of today) based on *when ticket counts were entered*,
  // not when the show date happens.
  const last7Start = new Date(today)
  last7Start.setDate(today.getDate() - 6)
  last7Start.setHours(0, 0, 0, 0)
  const last7End = new Date(today)
  last7End.setHours(23, 59, 59, 999)

  const upcomingShows = shows
    .filter((show) => {
      const showDate = toLocalDate(show.perf_date || show.date)
      if (!showDate) return false
      const day = new Date(showDate)
      day.setHours(0, 0, 0, 0)
      return day >= today
    })
    .sort(
      (a, b) =>
        getDateTime(a.perf_date || a.date) -
        getDateTime(b.perf_date || b.date)
    )

  // Only count ticket updates from upcoming shows (today and beyond)
  const showsWithTicketUpdatesLast7Days = upcomingShows.filter((show) => {
    if (!isTrackingTickets(show)) return false
    const raw = show.raw ?? {}
    const ts =
      raw.last_ticket_count_date ??
      raw.lastTicketCountDate ??
      raw.last_ticket_count_at ??
      raw.lastTicketCountAt ??
      raw.updated_at ??
      raw.updatedAt
    if (!ts) return false
    const d = new Date(ts)
    if (Number.isNaN(d.getTime())) return false
    return d.getTime() >= last7Start.getTime() && d.getTime() <= last7End.getTime()
  })

  const ticketCountsLast7Days = showsWithTicketUpdatesLast7Days.reduce(
    (sum, show) => sum + getResolvedTicketCount(show),
    0
  )

  const unsoldTicketsAllShows = upcomingShows.reduce((sum, show) => {
    if (!isTrackingTickets(show)) return sum
    const cap = getCapacity(show)
    if (cap <= 0) return sum
    const sold = getResolvedTicketCount(show)
    return sum + Math.max(0, cap - sold)
  }, 0)

  const upcomingGuarantees = upcomingShows.reduce((sum, show) => {
    const g = show.guarantee ?? show.raw?.guarantee
    if (g === undefined || g === null || g === '') return sum
    const num = typeof g === 'number' ? g : parseFloat(String(g).replace(/[^0-9.-]/g, ''))
    return sum + (Number.isNaN(num) ? 0 : num)
  }, 0)

  const formatGuarantee = (value: number) => {
    if (value === 0) return '—'
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(value)
  }

  const nextShow = upcomingShows[0]
  const nextShowDate = nextShow?.perf_date ?? nextShow?.date

  // ── Permission-based filtering (region & date) for team members ──
  const permissionFilteredShows = useMemo(() => {
    if (!showsConfig) return shows // Artist or no config → no filtering
    let result = shows

    // Region filter
    const rf = showsConfig.regionFilter
    if (rf?.enabled) {
      const hasRegionRules = (rf.countries?.length || 0) > 0 || (rf.states?.length || 0) > 0 || (rf.cities?.length || 0) > 0
      if (hasRegionRules) {
        const countriesLower = (rf.countries || []).map(c => c.toLowerCase())
        const statesLower = (rf.states || []).map(s => s.toLowerCase())
        const citiesLower = (rf.cities || []).map(c => c.toLowerCase())

        result = result.filter(show => {
          const showCity = (show.city || '').toLowerCase().trim()
          const showState = (show.state || (show.raw as any)?.state || '').toLowerCase().trim()
          const showCountry = ((show.raw as any)?.country || '').toLowerCase().trim()

          // Match if any of the region criteria match
          if (citiesLower.length > 0 && citiesLower.some(c => showCity.includes(c) || c.includes(showCity))) return true
          if (statesLower.length > 0 && statesLower.includes(showState)) return true
          if (countriesLower.length > 0 && countriesLower.includes(showCountry)) return true

          // If no field matches any rule, exclude the show
          return false
        })
      }
    }

    // Date filter
    const df = showsConfig.dateFilter
    if (df?.enabled) {
      const fromTime = df.from ? new Date(df.from).getTime() : null
      const toTime = df.to ? new Date(df.to + 'T23:59:59').getTime() : null

      result = result.filter(show => {
        const showDate = getDateTime(show.perf_date || show.date)
        if (fromTime && showDate < fromTime) return false
        if (toTime && showDate > toTime) return false
        return true
      })
    }

    return result
  }, [shows, showsConfig])

  // Apply search and status filter, then sort chronologically by performance date (earliest first)
  const searchLower = searchQuery.trim().toLowerCase()
  const filteredShows = permissionFilteredShows
    .filter((show) => {
      if (statusFilter && (show.status || '') !== statusFilter) return false
      if (!searchLower) return true
      const title = (show.event_title || show.title || show.venue || '').toLowerCase()
      const venue = (show.venue || '').toLowerCase()
      const city = (show.city || '').toLowerCase()
      const artist = (show.artist || '').toLowerCase()
      return title.includes(searchLower) || venue.includes(searchLower) || city.includes(searchLower) || artist.includes(searchLower)
    })
    .sort((a, b) => {
      const dateA = getDateTime(a.perf_date || a.date)
      const dateB = getDateTime(b.perf_date || b.date)
      const isUpcomingA = dateA >= today.getTime()
      const isUpcomingB = dateB >= today.getTime()

      // Upcoming shows first (soonest upcoming at top), then past shows (most recent past first).
      if (isUpcomingA !== isUpcomingB) return isUpcomingA ? -1 : 1
      if (isUpcomingA && isUpcomingB) return dateA - dateB
      return dateB - dateA
    })

  const modalMeta = {
    options: {
      title: 'Add Show',
      description: 'Choose how you want to add a show.',
    },
    manual: {
      title: 'Manual Add',
      description: 'Fill in required fields.',
    },
    csv: {
      title: 'CSV / Excel Import',
      description: 'Upload a CSV or Excel file to import shows.',
    },
    bandsintown: {
      title: 'Bandsintown Import',
      description: 'Fetch events from Bandsintown.',
    },
    'ticket-counter': {
      title: 'Update Tickets',
      description: 'Update ticket counts for upcoming shows.',
    },
  } as const

  return (
    <div className="p-8 max-w-6xl mx-auto">
      {/* ── Bandsintown Approval Modal ── */}
      {showBITApprovalModal && pendingBITShows.length > 0 && (
        <Modal
          isOpen={showBITApprovalModal}
          onClose={handleBITDismiss}
          title="Review Imported Shows"
          description={`We found ${pendingBITShows.length} show${pendingBITShows.length === 1 ? '' : 's'} from Bandsintown. Select the ones you'd like to add.`}
        >
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 cursor-pointer">
                <input
                  type="checkbox"
                  checked={pendingBITApproved.size === pendingBITShows.length}
                  onChange={toggleBITApproveAll}
                  className="h-4 w-4 rounded border-gray-300"
                />
                Select all ({pendingBITShows.length})
              </label>
              <Button
                type="button"
                onClick={handleBITApproveSelected}
                disabled={bitApprovalSaving || pendingBITApproved.size === 0}
              >
                {bitApprovalSaving
                  ? 'Saving…'
                  : `Approve ${pendingBITApproved.size === pendingBITShows.length ? 'All' : pendingBITApproved.size} Show${pendingBITApproved.size === 1 ? '' : 's'}`}
              </Button>
            </div>
            <div className="max-h-[360px] overflow-y-auto space-y-2">
              {pendingBITShows.map((show, idx) => {
                const isApproved = pendingBITApproved.has(idx)
                return (
                  <div
                    key={idx}
                    className={`rounded-lg border p-3 transition-colors cursor-pointer ${
                      isApproved
                        ? 'border-green-300 bg-green-50/50 dark:border-green-700 dark:bg-green-900/20'
                        : 'border-gray-200 bg-white dark:border-gray-600 dark:bg-gray-800'
                    }`}
                    onClick={() => toggleBITApproval(idx)}
                  >
                    <div className="flex items-start gap-3">
                      <input
                        type="checkbox"
                        checked={isApproved}
                        onChange={() => toggleBITApproval(idx)}
                        className="h-4 w-4 mt-0.5 rounded border-gray-300 flex-shrink-0"
                        onClick={(e) => e.stopPropagation()}
                      />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-gray-900 dark:text-gray-100 flex items-center gap-2">
                          <BandsintownBadge />
                          <span className="truncate">{show.event_title || show.venue || 'Untitled show'}</span>
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400 mt-1 flex flex-wrap gap-x-3">
                          {show.perf_date && <span>{formatDateForUI(show.perf_date)}</span>}
                          {show.venue && <span>{show.venue}</span>}
                          {(show.city || show.state) && (
                            <span>{[show.city, show.state].filter(Boolean).join(', ')}</span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
            <div className="flex justify-between pt-2">
              <button
                type="button"
                onClick={handleBITDismiss}
                className="text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
              >
                Dismiss all
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* ── Duplicate Detection Modal ── */}
      {showDuplicateModal && duplicateRows.length > 0 && (
        <Modal
          isOpen={showDuplicateModal}
          onClose={() => {
            setShowDuplicateModal(false)
            setDuplicateRows([])
            setNonDuplicateRows([])
            setDuplicateMergeSet(new Set())
          }}
          title="Duplicate Shows Found"
          description={`${duplicateRows.length} show${duplicateRows.length === 1 ? '' : 's'} already exist${duplicateRows.length === 1 ? 's' : ''} with the same date and venue. Choose which duplicates to merge (update with new data) or skip.`}
        >
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 cursor-pointer">
                <input
                  type="checkbox"
                  checked={duplicateMergeSet.size === duplicateRows.length}
                  onChange={() => {
                    if (duplicateMergeSet.size === duplicateRows.length) {
                      setDuplicateMergeSet(new Set())
                    } else {
                      setDuplicateMergeSet(new Set(duplicateRows.map((_, i) => i)))
                    }
                  }}
                  className="h-4 w-4 rounded border-gray-300"
                />
                Merge all duplicates ({duplicateRows.length})
              </label>
            </div>
            <div className="max-h-[360px] overflow-y-auto space-y-2">
              {duplicateRows.map((entry, idx) => {
                const isMerged = duplicateMergeSet.has(idx)
                return (
                  <div
                    key={idx}
                    className={`rounded-lg border p-3 transition-colors cursor-pointer ${
                      isMerged
                        ? 'border-blue-300 bg-blue-50/50 dark:border-blue-700 dark:bg-blue-900/20'
                        : 'border-gray-200 bg-white dark:border-gray-600 dark:bg-gray-800'
                    }`}
                    onClick={() => {
                      setDuplicateMergeSet((prev) => {
                        const next = new Set(prev)
                        if (next.has(idx)) next.delete(idx)
                        else next.add(idx)
                        return next
                      })
                    }}
                  >
                    <div className="flex items-start gap-3">
                      <input
                        type="checkbox"
                        checked={isMerged}
                        onChange={() => {
                          setDuplicateMergeSet((prev) => {
                            const next = new Set(prev)
                            if (next.has(idx)) next.delete(idx)
                            else next.add(idx)
                            return next
                          })
                        }}
                        className="h-4 w-4 mt-0.5 rounded border-gray-300 flex-shrink-0"
                        onClick={(e) => e.stopPropagation()}
                      />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                          <span className="truncate">{entry.incoming.event_title || entry.incoming.venue || 'Untitled show'}</span>
                          <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300">
                            Duplicate
                          </span>
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400 mt-1 flex flex-wrap gap-x-3">
                          {entry.incoming.perf_date && <span>{formatDateForUI(entry.incoming.perf_date)}</span>}
                          {entry.incoming.venue && <span>{entry.incoming.venue}</span>}
                          {(entry.incoming.city || entry.incoming.state) && (
                            <span>{[entry.incoming.city, entry.incoming.state].filter(Boolean).join(', ')}</span>
                          )}
                        </div>
                        <div className="text-[10px] text-gray-400 dark:text-gray-500 mt-0.5">
                          Matches existing: {entry.existing.venue || 'Unknown venue'} on {formatDateForUI(entry.existing.perf_date || entry.existing.date)}
                        </div>
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
            {nonDuplicateRows.length > 0 && (
              <div className="text-xs text-gray-500 px-1">
                {nonDuplicateRows.length} new (non-duplicate) show{nonDuplicateRows.length === 1 ? '' : 's'} will also be imported.
              </div>
            )}
            <div className="flex items-center justify-between pt-2 border-t border-gray-200">
              <button
                type="button"
                disabled={duplicateImporting}
                onClick={() => {
                  // Pass an empty set directly — do NOT rely on setState which is async
                  void handleDuplicateResolve(new Set())
                }}
                className="text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 disabled:opacity-50"
              >
                Skip all duplicates
              </button>
              <Button
                type="button"
                onClick={() => void handleDuplicateResolve()}
                disabled={duplicateImporting}
              >
                {duplicateImporting
                  ? 'Importing…'
                  : `Import ${nonDuplicateRows.length + duplicateMergeSet.size} show${(nonDuplicateRows.length + duplicateMergeSet.size) === 1 ? '' : 's'}${duplicateMergeSet.size > 0 ? ` (merge ${duplicateMergeSet.size})` : ''}`}
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {/* View-only banner for team members */}
      {isShowsViewOnly && (
        <div className="mb-4 px-4 py-2.5 rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 text-sm text-blue-700 dark:text-blue-300 flex items-center gap-2">
          <svg className="w-4 h-4 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" /></svg>
          You have view-only access to Shows. Contact the artist to request edit permissions.
        </div>
      )}

      <div className="mb-10 flex justify-between items-start">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight mb-2">Shows</h1>
          <p className="text-sm text-gray-500">Track ticket counts across all your shows</p>
        </div>
        {!isShowsViewOnly && (
          <div className="flex items-center gap-3">
            <Button
              variant="secondary"
              onClick={() => {
                setAddView('options')
                setIsAddOpen(true)
              }}
            >
              Add Show
            </Button>
            <Button
              onClick={() => {
                setAddView('ticket-counter')
                setIsAddOpen(true)
              }}
            >
              Update Tickets
            </Button>
          </div>
        )}
      </div>

      <Modal
        isOpen={isAddOpen}
        onClose={() => {
          setIsAddOpen(false)
          resetAddModal()
        }}
        title={isEditing && addView === 'manual' ? 'Edit Show' : modalMeta[addView].title}
        description={isEditing && addView === 'manual' ? 'Update show details.' : modalMeta[addView].description}
        maxWidthClassName="max-w-4xl"
      >
        {addView === 'options' ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[
              {
                key: 'ticket-counter',
                title: 'Ticket Counter',
                description: 'Update ticket counts for upcoming shows.',
              },
              { key: 'manual', title: 'Manual Add', description: 'Add a show with full details.' },
              { key: 'csv', title: 'CSV/Excel', description: 'Import shows from a CSV or Excel file.' },
              { key: 'bandsintown', title: 'Bandsintown', description: 'Pull in events from Bandsintown.' },
            ].map((option) => (
              <button
                key={option.key}
                type="button"
                className="text-left rounded-lg border border-gray-200 p-4 hover:border-gray-300 hover:bg-gray-50/50 transition-colors"
                onClick={() => setAddView(option.key as any)}
              >
                <div className="text-sm font-medium text-gray-900">{option.title}</div>
                <div className="text-xs text-gray-500 mt-1">{option.description}</div>
              </button>
            ))}
          </div>
        ) : addView === 'manual' ? (
          <form onSubmit={handleCreate} className="space-y-6">
            <div className="flex items-center justify-between">
              <div className="text-sm font-medium text-gray-900">Required details</div>
              <button
                type="button"
                onClick={() => setAddView('options')}
                className="text-xs text-gray-600 hover:text-gray-900"
              >
                Back to options
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-5">
              {(() => {
                const rendered: React.ReactNode[] = []
                const groupsSeen = new Set<string>()
                basicFields.forEach((field) => {
                  // Grouped fields: render all fields in the group as a single full-width 3-col row
                  if (field.group) {
                    if (groupsSeen.has(field.group)) return // already rendered
                    groupsSeen.add(field.group)
                    const groupFields = basicFields.filter((f) => f.group === field.group)
                    const isTicketGroup = field.group === 'tickets'
                    const isTrackingTickets = editData.ticket_tracking !== false
                    rendered.push(
                      <div key={`group-${field.group}`} className="md:col-span-2 space-y-2">
                        <div className={`grid grid-cols-1 md:grid-cols-3 gap-x-4 gap-y-5 ${isTicketGroup && !isTrackingTickets ? 'opacity-40 pointer-events-none select-none' : ''}`}>
                          {groupFields.map((gf) => (
                            <div key={gf.key}>
                              <label className="block text-sm font-medium mb-2">
                                {gf.label}
                                {gf.required ? <span className="text-red-500 ml-1">*</span> : null}
                              </label>
                              {renderField(gf)}
                            </div>
                          ))}
                        </div>
                        {isTicketGroup ? (
                          <button
                            type="button"
                            onClick={() => setEditData((prev) => ({ ...prev, ticket_tracking: !prev.ticket_tracking }))}
                            className={`text-xs font-medium transition-colors ${
                              isTrackingTickets
                                ? 'text-gray-500 hover:text-gray-700'
                                : 'text-blue-600 hover:text-blue-800'
                            }`}
                          >
                            {isTrackingTickets
                              ? 'Click to turn off ticket tracking for this show'
                              : 'Tickets are not being tracked for this show — click to track'}
                          </button>
                        ) : null}
                      </div>
                    )
                    return
                  }
                  // Normal fields
                  rendered.push(
                    <div key={field.key} className={field.fullWidth ? 'md:col-span-2' : ''}>
                      <label className="block text-sm font-medium mb-2">
                        {field.label}
                        {field.required ? <span className="text-red-500 ml-1">*</span> : null}
                        {field.key === 'artist' && dizeeArtistName && !isEditing && !artistFieldUnlocked ? (
                          <span className="ml-2 text-xs font-normal text-gray-400">from your profile</span>
                        ) : null}
                      </label>
                      {renderField(field)}
                    </div>
                  )
                })
                return rendered
              })()}
            </div>

            <div className="flex items-center justify-between pt-2 border-t border-gray-200">
              <button
                type="button"
                onClick={() => setShowAdvanced((v) => !v)}
                className="text-sm font-medium text-gray-600 hover:text-gray-900 underline underline-offset-4 transition-colors"
              >
                {showAdvanced ? '− Hide advanced fields' : '+ Show advanced fields'}
              </button>
            </div>

            {showAdvanced ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-5">
                {advancedFields.map((field) => (
                  <div key={field.key} className={field.type === 'textarea' ? 'md:col-span-2' : ''}>
                    <label className="block text-sm font-medium mb-2">{field.label}</label>
                    {renderField(field)}
                  </div>
                ))}
              </div>
            ) : null}

            <div className="flex gap-3 pt-4 border-t border-gray-200">
              <Button type="submit" disabled={isSubmitting} className="flex-1">
                {isSubmitting ? (isEditing ? 'Updating…' : 'Creating…') : (isEditing ? 'Update Show' : 'Create Show')}
              </Button>
              <Button
                type="button"
                variant="secondary"
                className="flex-1"
                onClick={() => {
                  setIsAddOpen(false)
                  resetAddModal()
                }}
              >
                Cancel
              </Button>
            </div>
          </form>
        ) : addView === 'csv' ? (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="text-sm font-medium text-gray-900">Upload CSV or Excel</div>
              <button
                type="button"
                onClick={() => setAddView('options')}
                className="text-xs text-gray-600 hover:text-gray-900"
              >
                Back to options
              </button>
            </div>
            <div className="relative">
              <input
                type="file"
                id="csv-file-input"
                accept=".csv,.xlsx,.xls"
                onChange={(e) => {
                  const file = e.target.files?.[0]
                  if (file) {
                    void handleCsvFileChange(file)
                  }
                }}
                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
              />
              <label
                htmlFor="csv-file-input"
                className="flex items-center justify-center gap-3 w-full px-6 py-8 rounded-lg border-2 border-dashed border-gray-300 bg-gray-50/30 hover:bg-gray-50 hover:border-gray-400 transition-all duration-200 cursor-pointer group"
              >
                <svg
                  className="w-8 h-8 text-gray-400 group-hover:text-gray-600 transition-colors duration-200"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                  />
                </svg>
                <div className="text-center">
                  <div className="text-sm font-medium text-gray-700 group-hover:text-gray-900 transition-colors duration-200">
                    {csvFileName || 'Choose a file or drag it here'}
                  </div>
                  <div className="text-xs text-gray-500 mt-1">CSV, XLSX, or XLS files</div>
                </div>
              </label>
            </div>
            {csvFileName ? (
              <div className="flex items-center gap-2 px-4 py-3 rounded-lg bg-gray-50 border border-gray-200">
                <svg className="w-4 h-4 text-green-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <div className="text-xs text-gray-600 flex-1 truncate">{csvFileName}</div>
              </div>
            ) : null}
            {csvLoading ? <div className="text-sm text-gray-500">Parsing file…</div> : null}
            {csvError ? <div className="text-sm text-red-500">{csvError}</div> : null}
            {csvRows.length > 0 ? (
              <div className="space-y-3">
                <div className="text-xs text-gray-500">{csvRows.length} rows ready to import</div>
                {dizeeArtistName ? (
                  <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-blue-50 border border-blue-200 text-xs text-blue-700">
                    <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span className="whitespace-nowrap">Artist:</span>
                    <input
                      type="text"
                      value={csvArtistOverride}
                      onChange={(e) => setCsvArtistOverride(e.target.value)}
                      className="flex-1 min-w-0 px-2 py-0.5 text-xs font-semibold rounded bg-blue-100/60 border-none text-blue-900 placeholder-blue-400 focus:outline-none focus:ring-1 focus:ring-blue-400 focus:bg-blue-100"
                      placeholder={dizeeArtistName}
                    />
                  </div>
                ) : null}
                <div className="max-h-[240px] overflow-y-auto space-y-2">
                  {csvRows.slice(0, 8).map((row, idx) => (
                    <div key={idx} className="rounded-lg border border-gray-200 p-3">
                      <div className="text-sm font-medium text-gray-900">
                        {row.event_title || row.venue || 'Untitled show'}
                      </div>
                      <div className="text-xs text-gray-500 mt-1">
                        {formatDateForUI(row.perf_date, 'No date')} • {row.city || 'Unknown city'}
                        {(csvArtistOverride.trim() || dizeeArtistName) ? ` • ${csvArtistOverride.trim() || dizeeArtistName}` : (row.artist ? ` • ${row.artist}` : '')}
                      </div>
                    </div>
                  ))}
                </div>
                <Button type="button" onClick={handleCsvImport} disabled={csvImporting}>
                  {csvImporting ? 'Importing…' : 'Import Shows'}
                </Button>
              </div>
            ) : null}
          </div>
        ) : addView === 'bandsintown' ? (
          <div className="space-y-4">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div className="flex items-center gap-2">
                {getBandsintownConnection() && (
                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300">
                    Connected
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2">
                {getBandsintownConnection() && (
                  <button
                    type="button"
                    onClick={() => {
                      clearBandsintownConnection()
                      setBandsintownAppId('')
                      setBandsintownArtist('')
                      setBandsintownEvents([])
                    }}
                    className="text-xs text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                  >
                    Disconnect
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => setAddView('options')}
                  className="text-xs text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200"
                >
                  Back to options
                </button>
              </div>
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Add your App ID once; we’ll remember it and use your profile artist so you can sync events anytime.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">Artist name</label>
                <input
                  type="text"
                  value={bandsintownArtist}
                  onChange={(e) => setBandsintownArtist(e.target.value)}
                  placeholder="Artist name"
                  className="ds-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Bandsintown App ID</label>
                <input
                  type="text"
                  value={bandsintownAppId}
                  onChange={(e) => setBandsintownAppId(e.target.value)}
                  placeholder="App ID"
                  className="ds-input"
                />
              </div>
            </div>
            <Button
              type="button"
              onClick={handleBandsintownPreview}
              disabled={bandsintownLoading || !bandsintownAppId.trim()}
            >
              {bandsintownLoading ? 'Fetching…' : getBandsintownConnection() ? 'Sync events' : 'Preview events'}
            </Button>
            {bandsintownError ? <div className="text-sm text-red-500">{bandsintownError}</div> : null}
            {bandsintownEvents.length > 0 ? (
              <div className="space-y-3">
                <div className="text-xs text-gray-500">
                  {bandsintownEvents.length} events ready to import
                </div>
                <div className="max-h-[240px] overflow-y-auto space-y-2">
                  {bandsintownEvents.slice(0, 8).map((event, idx) => (
                    <div key={idx} className="rounded-lg border border-gray-200 p-3">
                      <div className="text-sm font-medium text-gray-900 flex items-center gap-2 min-w-0">
                        <BandsintownBadge />
                        <span className="truncate min-w-0">{event.event_title || event.venue || 'Untitled show'}</span>
                      </div>
                      <div className="text-xs text-gray-500 mt-1">
                        {formatDateForUI(event.perf_date, 'No date')} • {event.city || 'Unknown city'}
                      </div>
                    </div>
                  ))}
                </div>
                <Button type="button" onClick={handleBandsintownImport} disabled={bandsintownImporting}>
                  {bandsintownImporting ? 'Importing…' : 'Import Events'}
                </Button>
              </div>
            ) : null}
          </div>
        ) : ticketImportMode ? (
          /* ── Ticket Import sub-view ─────────────────────────── */
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="text-sm font-medium text-gray-900">Import Ticket Data</div>
              <button
                type="button"
                onClick={() => {
                  setTicketImportMode(false)
                  setTicketImportText('')
                  setTicketImportFileName(null)
                  setTicketImportMatches([])
                  setTicketImportError(null)
                }}
                className="text-xs text-gray-600 hover:text-gray-900"
              >
                Back to ticket counter
              </button>
            </div>
            <p className="text-xs text-gray-500">
              Upload a file or paste text containing ticket counts. We'll match entries to your upcoming shows.
            </p>

            {/* File upload zone */}
            <div className="relative">
              <input
                ref={ticketImportFileRef}
                type="file"
                accept=".csv,.xlsx,.xls,.pdf,.png,.jpg,.jpeg,.gif,.webp,.txt"
                onChange={(e) => {
                  const file = e.target.files?.[0]
                  if (file) runTicketImport('file', file)
                  e.target.value = ''
                }}
                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
              />
              <label className="flex items-center justify-center gap-3 w-full px-6 py-6 rounded-lg border-2 border-dashed border-gray-300 bg-gray-50/30 hover:bg-gray-50 hover:border-gray-400 transition-all duration-200 cursor-pointer group">
                <svg
                  className="w-7 h-7 text-gray-400 group-hover:text-gray-600 transition-colors duration-200"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                  />
                </svg>
                <div className="text-center">
                  <div className="text-sm font-medium text-gray-700 group-hover:text-gray-900 transition-colors duration-200">
                    {ticketImportFileName || 'Upload a file'}
                  </div>
                  <div className="text-xs text-gray-500 mt-1">CSV, Excel, PDF, images, or text files</div>
                </div>
              </label>
            </div>

            {/* Divider */}
            <div className="relative flex items-center">
              <div className="flex-1 border-t border-gray-200" />
              <span className="px-3 text-xs text-gray-400 uppercase">or paste text</span>
              <div className="flex-1 border-t border-gray-200" />
            </div>

            {/* Text paste area */}
            <div>
              <textarea
                value={ticketImportText}
                onChange={(e) => setTicketImportText(e.target.value)}
                placeholder={'Paste ticket data here…\ne.g. "District ATLANTA - 626 sold (78%)"'}
                rows={4}
                className="ds-input w-full resize-none text-sm"
              />
              <div className="flex justify-end mt-2">
                <Button
                  type="button"
                  variant="secondary"
                  disabled={!ticketImportText.trim() || ticketImportParsing}
                  onClick={() => runTicketImport('text')}
                >
                  {ticketImportParsing ? 'Parsing…' : 'Parse Text'}
                </Button>
              </div>
            </div>

            {/* Loading */}
            {ticketImportParsing && (
              <div className="text-sm text-gray-500">Parsing and matching ticket data…</div>
            )}

            {/* Error */}
            {ticketImportError && (
              <div className="text-sm text-red-500 rounded-lg border border-red-200 bg-red-50 px-3 py-2">
                {ticketImportError}
              </div>
            )}

            {/* Matched results */}
            {ticketImportMatches.length > 0 && (
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <svg className="w-4 h-4 text-green-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <div className="text-xs text-gray-600">
                    Matched {ticketImportMatches.length} {ticketImportMatches.length === 1 ? 'entry' : 'entries'} to upcoming shows
                  </div>
                </div>
                <div className="max-h-[220px] overflow-y-auto space-y-2">
                  {ticketImportMatches.map((match) => (
                    <div
                      key={match.showId}
                      className="flex items-center justify-between rounded-lg border border-gray-200 bg-gray-50/50 p-3"
                    >
                      <div className="text-sm font-medium text-gray-900 truncate mr-3">
                        {match.showLabel}
                      </div>
                      <div className="flex items-center gap-3 flex-shrink-0">
                        <span className="text-sm tabular-nums text-gray-700">
                          {match.count.toLocaleString()} tickets
                        </span>
                        {match.percent !== undefined && (
                          <span className="text-xs text-gray-500">{match.percent}%</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
                <div className="flex items-center gap-2 justify-end">
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={() => {
                      setTicketImportMatches([])
                      setTicketImportFileName(null)
                      setTicketImportError(null)
                    }}
                  >
                    Clear
                  </Button>
                  <Button type="button" onClick={applyTicketImportMatches}>
                    Apply to Shows
                  </Button>
                </div>
              </div>
            )}
          </div>
        ) : (
          /* ── Regular ticket counter view ─────────────────────── */
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="text-sm font-medium text-gray-900">Update Tickets</div>
              <button
                type="button"
                onClick={() => setAddView('options')}
                className="text-xs text-gray-600 hover:text-gray-900"
              >
                Back to options
              </button>
            </div>
            {upcomingShows.length === 0 ? (
              <div className="text-sm text-gray-500">No upcoming shows to update.</div>
            ) : (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="text-xs text-gray-500">
                    {upcomingShows.length} upcoming shows
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      type="button"
                      variant="secondary"
                      onClick={() => setTicketImportMode(true)}
                    >
                      Import
                    </Button>
                    <Button
                      type="button"
                      variant="secondary"
                      onClick={() => saveAllTickets(upcomingShows)}
                      disabled={ticketDirty.size === 0}
                    >
                      Save All
                    </Button>
                  </div>
                </div>
                <div className="max-h-[360px] overflow-y-auto space-y-3">
                  {upcomingShows.map((show) => {
                    const showId = show.id
                    const countValue = ticketCounts[showId] ?? ''
                    const percentValue = ticketPercentages[showId] ?? ''
                    const isSaving = !!ticketSaving[showId]
                    const isShowTracked = isTrackingTickets(show)
                    if (!isShowTracked) {
                      return (
                        <div key={showId} className="rounded-lg border border-gray-200 border-dashed p-4 opacity-60">
                          <div className="flex items-center justify-between">
                            <div>
                              <div className="text-sm font-medium text-gray-900">
                                {show.event_title || show.venue || 'Untitled show'}
                              </div>
                              <div className="text-xs text-gray-500 mt-1">
                                {show.city || 'Unknown city'} •{' '}
                                {formatDateForUI(show.perf_date, 'No date')}
                              </div>
                            </div>
                            <button
                              type="button"
                              onClick={() => toggleTicketTracking(show)}
                              disabled={!!ticketSaving[showId]}
                              className="text-xs font-medium text-blue-600 hover:text-blue-800 transition-colors"
                            >
                              {ticketSaving[showId] ? 'Saving…' : 'Not tracking — click to track'}
                            </button>
                          </div>
                        </div>
                      )
                    }
                    const capValue = sellableCaps[showId] ?? ''
                    return (
                      <div key={showId} className="rounded-lg border border-gray-200 p-4 space-y-3">
                        <div className="flex items-center justify-between">
                          <div>
                            <div className="text-sm font-medium text-gray-900">
                              {show.event_title || show.venue || 'Untitled show'}
                            </div>
                            <div className="text-xs text-gray-500 mt-1">
                              {show.city || 'Unknown city'} •{' '}
                              {formatDateForUI(show.perf_date, 'No date')}
                            </div>
                          </div>
                          <Button type="button" onClick={() => saveTicketForShow(show)} disabled={isSaving}>
                            {isSaving ? 'Saving…' : 'Save'}
                          </Button>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">
                              Sellable Cap
                            </label>
                            <input
                              type="number"
                              value={capValue}
                              onChange={(e) => handleSellableCapChange(showId, e.target.value)}
                              className="ds-input"
                              placeholder="Venue capacity"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">
                              Ticket Count
                            </label>
                            <input
                              type="number"
                              value={countValue}
                              onChange={(e) => handleTicketCountChange(showId, e.target.value)}
                              className="ds-input"
                              placeholder="Enter tickets sold"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">
                              % Sold (optional)
                            </label>
                            <input
                              type="number"
                              value={percentValue}
                              onChange={(e) => handleTicketPercentageChange(showId, e.target.value)}
                              className="ds-input"
                              placeholder="Auto-calculates if blank"
                            />
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => toggleTicketTracking(show)}
                          disabled={!!ticketSaving[showId]}
                          className="text-xs font-medium text-gray-500 hover:text-gray-700 transition-colors"
                        >
                          {ticketSaving[showId] ? 'Saving…' : 'Click to turn off ticket tracking for this show'}
                        </button>
                      </div>
                    )
                  })}
                </div>
              </div>
            )}
          </div>
        )}
      </Modal>

      {/* Summary Stats */}
      <div className="mb-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-5">
          <div className="text-xs text-gray-500 mb-2 uppercase tracking-wider">Unsold Tickets</div>
          <div className="text-2xl font-semibold text-gray-900">
            {upcomingShows.length > 0 ? unsoldTicketsAllShows.toLocaleString() : '—'}
          </div>
          <div className="text-xs text-gray-500 mt-1">Upcoming shows</div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-5">
          <div className="text-xs text-gray-500 mb-2 uppercase tracking-wider">Ticket counts</div>
          <div className="text-2xl font-semibold text-gray-900">
            {upcomingShows.length > 0 ? ticketCountsLast7Days.toLocaleString() : '—'}
          </div>
          <div className="text-xs text-gray-500 mt-1">Last 7 days</div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-5">
          <div className="text-xs text-gray-500 mb-2 uppercase tracking-wider">Upcoming Guarantees</div>
          <div className="text-2xl font-semibold text-gray-900">{upcomingShows.length > 0 ? formatGuarantee(upcomingGuarantees) : '—'}</div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-5">
          <div className="text-xs text-gray-500 mb-2 uppercase tracking-wider">Next Up</div>
          {nextShow ? (
            <>
              <div className="text-sm font-medium text-gray-900 truncate">
                {nextShow.event_title || nextShow.venue}
              </div>
              <div className="text-xs text-gray-500 mt-1 truncate">
                {nextShow.city} • {formatDateForUI(nextShowDate, 'TBD')}
              </div>
            </>
          ) : (
            <div className="text-sm text-gray-500">No upcoming shows</div>
          )}
        </div>
      </div>

      {/* Search & Filters (above table) */}
      {shows.length > 0 ? (
        <div className="mb-4 flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[200px] max-w-sm">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </span>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by title, venue, city, or artist..."
              className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-gray-900 dark:focus:ring-gray-400 focus:border-transparent"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="text-sm border border-gray-200 dark:border-gray-600 rounded-lg px-3 py-2 bg-white dark:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-gray-900 dark:focus:ring-gray-400 focus:border-transparent"
          >
            <option value="">All</option>
            <option value="pending">Pending</option>
            <option value="confirmed">Confirmed</option>
            <option value="announced">Announced</option>
            <option value="private">Private</option>
            <option value="canceled">Canceled</option>
          </select>
          {(searchQuery.trim() || statusFilter) && (
            <button
              type="button"
              onClick={() => {
                setSearchQuery('')
                setStatusFilter('')
              }}
              className="text-sm text-gray-600 hover:text-gray-900"
            >
              Clear filters
            </button>
          )}
          <span className="text-sm text-gray-500">
            {filteredShows.length} of {shows.length} show{shows.length === 1 ? '' : 's'}
          </span>
          {filteredShows.length > 0 ? (
            <div className="flex items-center gap-3 ml-auto">
              {selectedShowIds.size === 0 ? (
                <button
                  type="button"
                  onClick={selectAllFiltered}
                  className="text-sm text-gray-600 hover:text-gray-900 focus:outline-none focus:underline"
                >
                  Select all
                </button>
              ) : (
                <>
                  <button
                    type="button"
                    onClick={() => setSelectedShowIds(new Set())}
                    className="text-sm text-gray-600 hover:text-gray-900 focus:outline-none focus:underline"
                  >
                    Clear
                  </button>
                  <Button
                    type="button"
                    onClick={handleBulkDelete}
                    disabled={isBulkDeleting}
                    className="bg-red-600 hover:bg-red-700 text-white text-sm py-1.5 px-3"
                  >
                    {isBulkDeleting ? 'Deleting…' : `Delete ${selectedShowIds.size} selected`}
                  </Button>
                </>
              )}
            </div>
          ) : null}
        </div>
      ) : null}

      {/* Shows Table */}
      {shows.length > 0 ? (
        <div className="bg-white rounded-lg border border-gray-200 dark:border-gray-700 overflow-visible">
          <table className="w-full border-collapse table-fixed">
            <colgroup>
              <col className={hideTickets ? 'w-[12%]' : 'w-[9%]'} />    {/* Date */}
              <col className={hideTickets ? 'w-[30%]' : 'w-[22%]'} />   {/* Venue */}
              <col className={hideTickets ? 'w-[18%]' : 'w-[11%]'} />   {/* City */}
              {!hideTickets && <col className="w-[9%]" />}    {/* Tickets */}
              {!hideTickets && <col className="w-[13%]" />}   {/* Venue Capacity */}
              {!hideTickets && <col className="w-[8%]" />}    {/* % Sold */}
              <col className={hideTickets ? 'w-[18%]' : 'w-[12%]'} />   {/* Status */}
              <col className="w-[4%]" />    {/* Actions */}
            </colgroup>
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50/50">
                <th className="text-left py-3 px-4 text-xs font-medium text-gray-700 uppercase tracking-wider">Date</th>
                <th className="text-left py-3 px-4 text-xs font-medium text-gray-700 uppercase tracking-wider">Venue</th>
                <th className="text-left py-3 px-4 text-xs font-medium text-gray-700 uppercase tracking-wider">City</th>
                {!hideTickets && <th className="text-right py-3 px-4 text-xs font-medium text-gray-700 uppercase tracking-wider">Tickets</th>}
                {!hideTickets && <th className="text-right py-3 px-4 text-xs font-medium text-gray-700 uppercase tracking-wider whitespace-nowrap">Venue Capacity</th>}
                {!hideTickets && <th className="text-right py-3 px-4 text-xs font-medium text-gray-700 uppercase tracking-wider">% Sold</th>}
                <th className="text-left py-3 px-4 text-xs font-medium text-gray-700 uppercase tracking-wider">Status</th>
                <th className="py-3 px-4 text-xs font-medium text-gray-700 uppercase tracking-wider"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filteredShows.length === 0 ? (
                <tr>
                  <td colSpan={hideTickets ? 5 : 8} className="py-12 text-center text-sm text-gray-500">
                    No shows match your search or filters. Try adjusting or{' '}
                    <button
                      type="button"
                      onClick={() => {
                        setSearchQuery('')
                        setStatusFilter('')
                      }}
                      className="text-gray-900 font-medium hover:underline"
                    >
                      clear filters
                    </button>
                    .
                  </td>
                </tr>
              ) : (
              filteredShows.map((show) => {
                const venueCapacity = show.sellable_cap || null
                const percentSoldRaw = show.tix_sold_percent ?? null
                const percentSold = percentSoldRaw !== null ? Number(percentSoldRaw) : null
                const key = (show as any)._id || show.id || `${show.title}-${show.perf_date || show.date}`
                const d = show.date || show.perf_date
                const isSelected = selectedShowIds.has(show.id)
                const isTracked = isTrackingTickets(show)
                return (
                  <tr
                    key={key}
                    className={`transition-colors hover:bg-gray-50/50 dark:hover:bg-white/[0.04] cursor-pointer ${isSelected ? 'bg-gray-100 dark:bg-white/[0.08]' : ''}`}
                    onClick={() => toggleShowSelection(show.id)}
                  >
                    <td className="py-3 px-4 text-sm text-gray-900 whitespace-nowrap">
                      {formatDateForUI(d)}
                    </td>
                    <td className="py-3 px-4 text-sm text-gray-600 overflow-hidden">
                      <div className="flex items-center gap-2 min-w-0">
                        {(show.source === 'bandsintown' || (show.raw as any)?.source === 'bandsintown') && <BandsintownBadge />}
                        <span className="truncate">{show.venue}</span>
                      </div>
                    </td>
                    <td className="py-3 px-4 text-sm text-gray-600 truncate">
                      {show.city}
                    </td>
                    {!hideTickets && (
                      isTracked ? (
                        <>
                          <td className="py-3 px-4 text-sm text-right text-gray-900 font-medium">
                            {getTicketCount(show).toLocaleString()}
                          </td>
                          <td className="py-3 px-4 text-sm text-right text-gray-600">
                            {venueCapacity ? venueCapacity.toLocaleString() : '-'}
                          </td>
                          <td className="py-3 px-4 text-sm text-right text-gray-900 font-medium">
                            {percentSold !== null && !Number.isNaN(percentSold) ? `${percentSold.toFixed(1)}%` : '-'}
                          </td>
                        </>
                      ) : (
                        <td colSpan={3} className="py-3 px-4 text-sm text-center text-gray-400 italic">
                          Not tracking tickets
                        </td>
                      )
                    )}
                    <td className="py-3 px-4" onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center gap-2">
                        <select
                          value={showStatusOptions.includes((show.status || '') as ShowStatus) ? (show.status as ShowStatus) : 'pending'}
                          onChange={(e) => handleShowStatusChange(show.id, e.target.value as ShowStatus)}
                          disabled={!!statusSaving[show.id]}
                          className={`text-xs font-medium px-2 py-1 rounded border-0 focus:ring-2 focus:ring-gray-900 dark:focus:ring-gray-400 cursor-pointer disabled:cursor-not-allowed disabled:opacity-60 ${
                            show.status === 'confirmed'
                              ? 'bg-green-50 text-green-700 dark:bg-green-900/30 dark:text-green-300'
                              : show.status === 'announced'
                                ? 'bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300'
                                : show.status === 'canceled'
                                  ? 'bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-300'
                                  : show.status === 'private'
                                    ? 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300'
                                    : 'bg-gray-50 text-gray-700 dark:bg-gray-700 dark:text-gray-300'
                          }`}
                        >
                          {showStatusOptions.map((opt) => (
                            <option key={opt} value={opt}>
                              {opt.charAt(0).toUpperCase() + opt.slice(1)}
                            </option>
                          ))}
                        </select>
                        {/* no inline saving indicator */}
                      </div>
                    </td>
                    <td className="py-3 px-4 text-center relative" onClick={(e) => e.stopPropagation()}>
                      <div className="relative inline-block">
                        <button
                          onClick={(e) => {
                            e.stopPropagation()
                            if (openDropdown === show.id) {
                              setOpenDropdown(null)
                              setDropdownPosition(null)
                              return
                            }
                            const rect = e.currentTarget.getBoundingClientRect()
                            setDropdownPosition({
                              top: rect.bottom + 8,
                              right: window.innerWidth - rect.right,
                            })
                            setOpenDropdown(show.id)
                          }}
                          className="text-gray-600 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-gray-900 focus:ring-offset-1 rounded p-1"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z" />
                          </svg>
                        </button>
                        {openDropdown === show.id && dropdownPosition && createPortal(
                          <>
                            <div
                              className="fixed inset-0 z-40"
                              onClick={() => {
                                setOpenDropdown(null)
                                setDropdownPosition(null)
                              }}
                            />
                            <div
                              className="fixed w-48 bg-white rounded-md shadow-lg border border-gray-200 z-50 py-1 dark:bg-gray-800 dark:border-gray-600"
                              style={{ top: dropdownPosition.top, right: dropdownPosition.right }}
                            >
                              <button
                                onClick={(e) => {
                                  e.stopPropagation()
                                  startEditShow(show)
                                  setOpenDropdown(null)
                                  setDropdownPosition(null)
                                }}
                                className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-gray-700 transition-colors"
                              >
                                Edit
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation()
                                  handleDeleteShow(show.id)
                                  setDropdownPosition(null)
                                }}
                                className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-gray-700 transition-colors"
                              >
                                Delete
                              </button>
                            </div>
                          </>,
                          document.body
                        )}
                      </div>
                    </td>
                  </tr>
                )
              })
              )}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
          <p className="text-sm text-gray-500">No shows found</p>
        </div>
      )}
    </div>
  )
}
